// Boston, Faneuil Hall: "Buskers by the grasshopper". A warm, clear early-June night, about an hour after sunset, in
// Dock Square: the viewer stands on the Freedom Trail where it comes up Congress Street's east sidewalk at the square's
// south-west corner (42.35973 N, 71.05694 W), looking north-east to east (bearings 8 to 116 degrees). Measured on the
// OpenStreetMap footprints (local metres, x east, y north, origin 42.3600 N 71.0560 W; the viewer at x -77, y -30):
//   - Sam Adams on his granite pedestal (25.7 deg, 31 m), west of the hall, facing west towards City Hall;
//   - Faneuil Hall's west gable front (43 to 66.6 deg, 48 to 60 m), seven bays of white-arched windows on three floors,
//     the pediment with its lunette and two ox-eyes, and its long south side receding to the south-east corner (72.3 deg,
//     79 m); the cupola stands at the hall's EAST end (62.9 deg, 78 m), so from here its belfry, dome and the gilded
//     grasshopper rise over the gable's right-hand rake;
//   - down South Market Street, between the hall's corner and the pavilion: Quincy Market's granite south side under
//     its glass canopy, its copper dome on the octagonal drum (75.3 deg, 181 m), the marketplace's lit trees and carts;
//   - the one-storey pavilion at Congress and North streets close on the right (9 to 34 m): a raw bar (Briny's, invented)
//     with diners in its windows and its sign on the roof (the city's billboard);
//   - behind it the top of the Custom House Tower and its clock (105.5 deg, 288 m) and the India Building at the edge.
//   - on the left: the Blackstone Block's brick on Union Street (100 to 170 m) and a low glass shop pavilion on North St.
// The brief hoped for Quincy Market's portico and columns too: from Dock Square the hall itself hides them (the portico
// faces the hall's east end, 21 m away), and from the plaza between the two the hall is so close that its pediment no
// longer reads; so Quincy Market shows down the lane as its long side, canopy and dome, with the portico's corner
// column and the foot of its pediment just past Faneuil Hall's corner (the market drawn 8 m south of its footprint,
// with the dome, so that the dome clears Faneuil Hall and still stands on the market's middle). The harbour, the Old
// State House (down Congress Street behind the viewer) and the North End's streets (behind the Blackstone Block) are
// out of sight.
// Projection: a cylindrical panorama, 5 px a degree of height everywhere (true heights for distance); across, the hall
// keeps 5 px a degree (its true proportions) and the rest is pressed to about 4.2 to 4.7, never reordered (colOf).
// render(t, { crowd }) thins the people (CROWD); crowd 0 draws nobody, the flower cart and the raw bar stand unattended.
//
// The loop's timeline (seconds):
//   all loop  the busker works his circle in front of the hall (juggling, patter), the cupola's floodlights glow, bats
//             hawk moths round the cupola, the string lights in the honey locusts twinkle, windows of the Blackstone Block
//             and the towers switch on and off, cloud drifts east, pigeons peck by the statue, the raw bar's diners eat,
//             the flower seller tidies her buckets, far traffic glints across the Greenway at the end of the lane
//   4-18      a walker crosses near the viewer (more at 84-98, 214-228); couples cross behind the circle (lane B)
//   12-40     a jet climbs out high over the harbour, strobes blinking (again 92-120, 172-200)
//   18-26     the Great Hall's chandeliers come up for an evening concert (they go down at 222-232)
//   22-30     the kid runs at the pigeons, they fly up onto Sam Adams (back down at 60); again 102-110 (140), 172-180 (210)
//   30-46     a pedicab rolls across the square, left to right (back right to left at 150-166)
//   40-112    BIG: the busker's finale: the tall unicycle goes up, a volunteer steadies it (44-60), he mounts (52-58),
//             juggles three, four, then five clubs (60-84), a high throw (84-87), the circle applauds (87-96), he
//             dismounts and passes the hat (96-112)
//   52-62     someone buys a bunch of peonies at the flower cart (again 176-186)
//   60-72     a couple with ice cream comes in and sits on the statue's platform (until 196, then leaves)
//   64-90     an arrival's landing light sinks towards Logan down the lane (again 200-226)
//   128-188   BIG: a Freedom Trail guide in colonial dress leads his tour along the red line past the busker, stops by
//             the hall's south-west corner (145-163), points up at the grasshopper, which stirs and glints (151-156), and
//             leads them on down the lane to Quincy Market
//   196-234   BIG: the evening breeze: the string lights sway harder and harder, the banners stir, and the grasshopper
//             swings round into the floodlight and gleams gold (206-224) before the breeze settles
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, makePitch, gradeAd, text, textWidth, FONT3 } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Viewpoint and projection ================= */
const VX = -77, VY = -30, EYE = 1.6;                // the viewer, metres (see the header)
const HZ = 184, SV = 5;                             // the horizon row, rows a degree of elevation
const DEG = 180 / Math.PI;
// bearing (degrees) -> column: the hall keeps 5 px a degree, either side is pressed, never reordered
const KN = [[8, 0], [43, 130], [72.3, 276.5], [116, 480.6]];
function colOf(b) {
  for (let i = 1; i < KN.length; i++) if (b <= KN[i][0] || i === KN.length - 1) {
    const [b0, c0] = KN[i - 1], [b1, c1] = KN[i];
    return c0 + (c1 - c0) * (b - b0) / (b1 - b0);
  }
  return 0;
}
const bearing = (x, y) => (Math.atan2(x - VX, y - VY) * DEG + 360) % 360;
const dist = (x, y) => Math.hypot(x - VX, y - VY);
/** [col, row, distance] of a point x, y (metres on the ground plan) at height z (metres) */
const proj = (x, y, z = 0) => { const d = dist(x, y); return [colOf(bearing(x, y)), HZ - SV * Math.atan2(z - EYE, d) * DEG, d]; };
/** the row of the feet of someone d metres away, and their height in px for h metres */
const feetRow = d => HZ + SV * Math.atan2(EYE, d) * DEG;
const tallPx = (d, h) => SV * (Math.atan2(EYE, d) + Math.atan2(h - EYE, d)) * DEG;

/* ================= Layout (from the projection, rounded) ================= */
const ACCENT = '#44bb11';                           // the city's (the Green Line's green)
// Faneuil Hall: footprint corners, the cornice and the pediment's apex (heights from photographs: 52 ft and 69 ft)
const FH = { NW: [-36, 14], SW: [-33, -11], SE: [-2, -6], CORN: 15.8, APEX: 21, WEST_W: 25.2, SOUTH_W: 31.4 };
const CUP = { x: -7.5, y: 5.5 };                    // the cupola, at the east end of the ridge
const STATUE = { x: -63.5, y: -1.9 };
const CIRCLE = { x: -57, y: -12, r: 3.4 };          // the busker's pitch in front of the hall
const FLOWER = { x: -66, y: -12 };                  // the flower cart
const PAV = { NE: [-43, -26], NW: [-64, -29], W: [-68, -33], S: [-54, -43], h: 4.5 };   // the raw bar's pavilion
const BB = { x: 334, y: 98, w: 46, h: 20 };         // Briny's roof sign (the city's billboard)
// the Freedom Trail's red line: up Congress Street's sidewalk, then east along the hall's south side to the market
const TRAIL = [[-75, -34], [-76.5, -24], [-77, -21], [-39, -16], [2, -11], [30, -8]];

/* ================= Timeline ================= */
const ACT = { setup: 40, vol: 44, mount: 52, up: 58, clubs3: 60, clubs4: 70, clubs5: 78, high: 84, clap: 87, down: 92, hat: 96, end: 112 };
const CLAPS = [[18, 21], [ACT.clap, ACT.clap + 9], [140, 143], [181, 184], [222, 225]];
const HALL = { on: 18, full: 26, dim: 222, off: 232 };              // the Great Hall's chandeliers
const CHASE = [22, 102, 172];                                       // the kid runs at the pigeons
const PERCH = 34;                                                   // the pigeons stay on the statue this long
const PEDI = [{ t0: 30, t1: 46, dir: 1 }, { t0: 150, t1: 166, dir: -1 }];
const SALE = [52, 176];                                             // a bunch of flowers sold
const ICE = { t0: 60, sit: 72, rise: 196, t1: 210 };                // the couple with ice cream
const JETS = [{ t0: 12, t1: 40 }, { t0: 92, t1: 120 }, { t0: 172, t1: 200 }];
const ARRIVE = [{ t0: 64, t1: 90 }, { t0: 200, t1: 226 }];
const TOUR = { t0: 128, stop: 145, point: 148, go: 163, t1: 188 };
const GLINT = [151, 156];
const BREEZE = { t0: 196, peak: 206, fall: 224, t1: 234 };
const WALK_A = [{ t0: 4, t1: 18, dir: 1 }, { t0: 84, t1: 98, dir: -1 }, { t0: 214, t1: 228, dir: 1 }];
const WALK_B = [{ t0: 8, t1: 34, dir: -1 }, { t0: 70, t1: 100, dir: 1 }, { t0: 112, t1: 140, dir: -1 }, { t0: 190, t1: 220, dir: 1 }];
// The people: 19 who stay (the busker and his circle of ten, the flower seller, a parent and child, a sitter on the statue's
// platform, a photographer, two diners and the waiter), seven diners inside Briny's, plus walkers, the tour, the pedicab
// and the couple with ice cream; about half by default. render(t, { crowd }) shows those whose rank is under crowd; rank 0
// (the busker and his volunteer, the flower seller, the waiter, the kid and parent, the guide) stays while anybody does;
// crowd 0 draws nobody and takes the clubs, the unicycle, the hat, the lantern and the pedicab along.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { busker: 0, vendor: 0, waiter: 0, kid: 0, guide: 0, ice: 0.3,
  sitter: 0.58, photo: 0.5, diners: 0.12, tour: [0.1, 0.3, 0.55, 0.75, 0.9], walkA: 0.4, walkB: 0.64, pedi: 0.26, buyer: 0.48 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, v], ...] sorted by t; the value at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}
const inWin = (t, a, b) => t >= a && t < b;
const inAny = (t, list) => list.some(([a, b]) => t >= a && t < b);
/** the breeze: 0 calm .. 1 at its height (with a small puff when the guide points) */
const breeze = t => 0.12 + 0.88 * smooth(BREEZE.t0, BREEZE.peak, t) * (1 - smooth(BREEZE.fall, BREEZE.t1, t)) + 0.35 * env(t, GLINT[0] - 1, GLINT[1] + 1, 1.5);

/* ================= The sky: an early-June night an hour after sunset, looking east over the harbour ================= */
// Clear and warm. The east is a deep blue, paling down to the city's glow over the harbour and downtown on the right;
// a few rags of cloud catch the glow from below and drift east; the brighter stars are out.
const SKY = ['#090f2c', '#0b1232', '#0e1638', '#111a3f', '#151f46', '#19244d', '#1e2a54', '#24305b', '#2b3662', '#333c68',
  '#3c426c', '#46486f', '#514d70', '#5c5270', '#68576f', '#745c6c'];
const skyV = (x, y) => Math.pow(clamp(y / HZ, 0, 1), 1.55) * 0.7 + 0.2 * smooth(240, 480, x) * smooth(60, HZ, y)
  + 0.08 * Math.exp(-(((x - 300) / 70) ** 2)) * smooth(110, HZ, y) - 0.06 * smooth(160, 0, x) * smooth(0, 120, y);
function renderSky() {
  const p = pix(W, HZ + 6);
  for (let y = 0; y < HZ + 6; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(SKY, skyV(x, y) + (vnoise(x / 60, y / 9, 3) - 0.5) * 0.035, x, y));
  return p.done();
}
const hazeAt = (x, y) => SKY[Math.min(SKY.length - 1, Math.round(clamp(skyV(x, y), 0, 1) * (SKY.length - 1)))];
const STARS = [];
{
  const r = rng(733);
  while (STARS.length < 38) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 96);
    if (skyV(x, y) > 0.24) continue;
    STARS.push({ x, y, k: 7 + Math.floor(r() * 29), ph: r(), b: 0.3 + r() * r() * 0.7, c: r() < 0.3 ? '#ffe8c8' : r() < 0.55 ? '#cfdcff' : '#eef0ff' });
  }
}
/* rags of cloud, tileable (period W): soft lumpy banks, their undersides lit warm by the city */
function makeCloud(seed, h, banks, ramp, a) {
  const r = rng(seed), blobs = [];
  for (let b = 0; b < banks; b++) {
    const bx0 = r() * W, by = h * (0.1 + r() * 0.8), n = 4 + Math.floor(r() * 5), span = 30 + r() * 70;
    for (let i = 0; i < n; i++) blobs.push({ x: bx0 + (r() - 0.5) * span, y: by + (r() - 0.5) * 6, rx: 10 + r() * 22, ry: 3 + r() * 4 });
  }
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0, below = 0;
    for (const s of blobs) {
      let dx = x - s.x; dx -= Math.round(dx / W) * W;
      const q = 1 - (dx / s.rx) ** 2 - ((y - s.y) / s.ry) ** 2;
      if (q > v) { v = q; below = (y - s.y) / s.ry; }
    }
    v *= 0.7 + 0.6 * vnoise(x / 7, y / 3, seed, Math.round(W / 7));
    if (v > 0.1 + bayer(x, y) * 0.45) p.set(x, y, pick(ramp, 0.25 + below * 0.45 + v * 0.3, x, y, 0.7), a);
  }
  return p.done();
}
/* ================= Windows that switch on and off ================= */
const WINS = [];
function addTog(r, x, y, w, h, col, duty = 0.3 + r() * 0.45) {
  WINS.push({ x, y, w, h, c: col, k: 2 + Math.floor(r() * 5), ph: r(), duty });
}
const WARMW = ['#ffd890', '#f8c070', '#ffe6b0', '#f0b060'], COOLW = ['#e4ecf8', '#c8d8ec', '#d8e2f0'];
const winLit = (w, t) => frac(w.k * t / LOOP + w.ph) < w.duty;
function drawWins(a, b, t) { for (let i = a; i < b; i++) { const w = WINS[i]; if (winLit(w, t)) R(w.x, w.y, w.w, w.h, w.c); } }

/* ================= A wall in the panorama ================= */
// A vertical face from ground point A to B, its top at topFn(u) metres (u 0..1 along it). For every column it covers,
// the point of the face seen there is found (bisection: columns grow with bearing), and every row of it gets the
// face's own coordinates: s metres along from A and z metres up. mat(s, z, d, col, row) returns a colour or null.
function uAtCol(A, B, c) {
  const ca = colOf(bearing(A[0], A[1])), cb = colOf(bearing(B[0], B[1])), inc = cb > ca;
  let lo = 0, hi = 1;
  for (let k = 0; k < 28; k++) {
    const m = (lo + hi) / 2, cm = colOf(bearing(lerp(A[0], B[0], m), lerp(A[1], B[1], m)));
    if ((cm < c) === inc) lo = m; else hi = m;
  }
  return (lo + hi) / 2;
}
function paintFace(p, A, B, topFn, mat, oy = 0) {
  const ca = colOf(bearing(A[0], A[1])), cb = colOf(bearing(B[0], B[1])), len = Math.hypot(B[0] - A[0], B[1] - A[1]);
  const c0 = Math.round(Math.min(ca, cb)), c1 = Math.round(Math.max(ca, cb)) - 1;
  for (let c = Math.max(0, c0); c <= Math.min(W - 1, c1); c++) {
    const u = uAtCol(A, B, c + 0.5), x = lerp(A[0], B[0], u), y = lerp(A[1], B[1], u), d = dist(x, y), top = topFn(u);
    const rTop = Math.round(HZ - SV * Math.atan2(top - EYE, d) * DEG), rBase = Math.round(HZ + SV * Math.atan2(EYE, d) * DEG);
    for (let r = Math.max(0, rTop); r <= Math.min(H - 1, rBase); r++) {
      const z = r === rTop ? top : Math.max(0, EYE + d * Math.tan((HZ - (r + 0.5)) / SV / DEG));
      const col = mat(u * len, Math.min(z, top), d, c, r, r === rTop, r === rBase);
      if (col) p.set(c, r - oy, col);
    }
  }
}
const flatTop = h => () => h;

/* ================= The far side: the Blackstone Block, the towers, the lane to Quincy Market ================= */
// Simple blocks seen across the square: [bearing from, bearing to, distance m, height m, ramp, windows lit share]
const BRICKF = ['#170e12', '#22141a', '#2e1a1e', '#3c2224', '#4c2c2a'];
const STONEF = ['#16161e', '#1e1e28', '#282834', '#343440', '#42424c'];
const BLOCKS = [
  [6, 10.5, 168, 12, BRICKF, 0.5, 'oyster'], [10.5, 15.5, 142, 16, BRICKF, 0.45], [15.5, 20, 100, 27, STONEF, 0.35],
  [20, 24.6, 106, 21, BRICKF, 0.4], [24.6, 33, 96, 14, BRICKF, 0.5], [33, 38, 210, 24, STONEF, 0.3], [38, 44, 190, 17, BRICKF, 0.35],
  [77.6, 80.5, 520, 25, BRICKF, 0.4], [80.5, 83.6, 470, 14, STONEF, 0.3],
];
function paintBlocks(p, r) {
  for (const [b0, b1, d, h, ramp, lit, tag] of BLOCKS) {
    const c0 = Math.round(colOf(b0)), c1 = Math.round(colOf(b1)), top = Math.round(HZ - SV * Math.atan2(h - EYE, d) * DEG);
    const fl = Math.max(2, Math.round(SV * Math.atan2(3.2, d) * DEG)), haze = clamp((d - 80) / 500, 0.15, 0.6);
    for (let x = c0; x < c1; x++) for (let y = top; y < HZ + 3; y++) {
      let v = 0.45 + (x === c0 ? 0.25 : 0) + (x === c1 - 1 ? -0.2 : 0) + (y === top ? 0.3 : 0) + (vnoise(x / 3, y / 4, c0) - 0.5) * 0.15;
      p.set(x, y, mixHex(pick(ramp, v, x, y, 0.6), hazeAt(x, y), haze));
    }
    if (tag === 'oyster') for (let x = c0 + 2; x < c1 - 2; x++) { p.set(x, top + 6, '#e8c070'); p.set(x, top + 7, '#a86a3a'); }   // a lit fascia over a restaurant door
    for (let y = top + 2; y < HZ - 1; y += fl) for (let x = c0 + 1; x < c1 - 1; x += 2) {
      const v = r();
      if (v < lit * 0.6) p.set(x, y, mixHex(WARMW[Math.floor(r() * 4)], hazeAt(x, y), haze * 0.6));
      else if (v < lit) addTog(r, x, y, 1, 1, mixHex(WARMW[Math.floor(r() * 4)], hazeAt(x, y), haze * 0.6));
    }
  }
}
// The India Building at the right edge (a 1960s office slab, 11 floors) and the six-storey block on Merchants Row
const INDIA = { A: [20, -60], B: [-12, -60], h: 43 };
const SIX = { NW: [-11, -28], SW: [-7, -52], h: 21 };
function paintIndia(p, r) {
  paintFace(p, INDIA.A, INDIA.B, flatTop(INDIA.h), (s, z, d, c, row, top) => {
    if (top) return '#5a5a68';
    const fl = (z % 3.6) / 3.6, bay = (s % 2.2) / 2.2;
    if (fl > 0.35 && fl < 0.8 && bay > 0.15 && bay < 0.85) {
      const v = hash(Math.floor(s / 2.2), Math.floor(z / 3.6), 51);
      if (v < 0.22) return mixHex(COOLW[Math.floor(v * 13) % 3], '#1a1c2a', 0.25);
      return '#141620';
    }
    return pick(STONEF, 0.5 + (hash(c, row, 52) - 0.5) * 0.15, c, row);
  });
  paintFace(p, SIX.NW, SIX.SW, flatTop(SIX.h), (s, z, d, c, row, top) => {
    if (top) return '#4a3e3a';
    const fl = (z % 3.3) / 3.3, bay = (s % 2.4) / 2.4;
    if (fl > 0.3 && fl < 0.78 && bay > 0.25 && bay < 0.75) return hash(Math.floor(s / 2.4), Math.floor(z / 3.3), 61) < 0.3 ? mixHex(WARMW[1], '#20141a', 0.2) : '#1a1218';
    return pick(BRICKF, 0.55 + (hash(c, row, 62) - 0.5) * 0.2, c, row);
  });
}
// The Custom House Tower (1915), 288 m off: granite, its floodlit crown, the colonnaded observation deck, the clock
// stage and the pyramid, hazed and kept a step dimmer than the hall.
const CH = { x: 200, y: -107, w: 18, h: 151 };
const CH_COL = Math.round(proj(CH.x, CH.y)[0]);
const chRow = z => Math.round(HZ - SV * Math.atan2(z - EYE, dist(CH.x, CH.y)) * DEG);
function paintCustomHouse(p) {
  const cx = CH_COL, haze = 0.32, put = (x, y, v) => p.set(x, y, mixHex(pick(['#24222a', '#3e3a42', '#6a645e', '#a89c88', '#dcd0b4'], v, x, y, 0.5), hazeAt(x, y), haze));
  const top = chRow(CH.h), pyrB = chRow(CH.h - 13), clkB = chRow(CH.h - 26), colT = clkB + 1, colB = chRow(CH.h - 38);
  for (let y = top - 4; y < top; y++) put(cx, y, 0.85);                                             // the finial
  for (let y = top; y < pyrB; y++) { const hw = Math.round((y - top) / (pyrB - top) * 6); for (let x = cx - hw; x <= cx + hw; x++) put(x, y, 0.82 - (x > cx ? 0.2 : 0) + (x === cx - hw ? 0.1 : 0)); }
  for (let y = pyrB; y < clkB; y++) for (let x = cx - 6; x <= cx + 6; x++) {                        // the clock stage, its face lit
    const dx = x - cx, dy = y - Math.round((pyrB + clkB) / 2), face = dx * dx + dy * dy <= 6;
    put(x, y, face ? (dx * dx + dy * dy <= 1 ? 0.55 : 1) : 0.66 - (x > cx + 3 ? 0.2 : 0) + (Math.abs(dx) === 6 ? 0.12 : 0));
  }
  for (let x = cx - 7; x <= cx + 7; x++) put(x, clkB, 0.95);                                       // the cornice
  for (let y = colT; y < colB; y++) for (let x = cx - 7; x <= cx + 7; x++) put(x, y, (x - cx + 7) % 3 === 2 ? 0.25 : 0.8 - (y - colT) * 0.03);   // the floodlit colonnade
  for (let x = cx - 7; x <= cx + 7; x++) put(x, colB, 0.7);
  for (let y = colB + 1; y < HZ + 2; y++) for (let x = cx - 6; x <= cx + 6; x++) {                  // the shaft
    const win = (y - colB) % 3 === 2 && (x - cx + 6) % 2 === 1;
    put(x, y, win ? (hash(x, y, 71) < 0.25 ? 0.95 : 0.1) : 0.4 - (y - colB) * 0.004 - (x > cx + 3 ? 0.12 : 0) + (x === cx - 6 ? 0.08 : 0));
  }
}

/* ================= Quincy Market down the lane ================= */
// The long granite market hall (two floors, 9.4 m to the cornice): its south side runs away from us almost edge-on, the
// west end pavilion first, then the long middle under the 1970s glass canopy (its shed's end glazed and joined to the
// wall), then the east pavilion; the west portico's corner column and the foot of its pediment show past Faneuil Hall's
// corner. The rotunda's copper dome on its octagonal drum, with the lantern, rises behind the near cornice from the
// hall's ridge. The hall is drawn 8 m south of its footprint, so that the dome (moved the same way, to clear Faneuil
// Hall) stands on its middle; between it and the pavilion, the plaza's honey locusts with their lights and the pushcarts.
const QM = { SW: [17, -2.7], A: [38, 0], B: [163, 16], SE: [181, 18.3], NW: [15, 13.2], corn: 9.4, apex: 13.4 };
const QM_LEN = Math.hypot(QM.SE[0] - QM.SW[0], QM.SE[1] - QM.SW[1]), QM_A = Math.hypot(QM.A[0] - QM.SW[0], QM.A[1] - QM.SW[1]), QM_B = Math.hypot(QM.B[0] - QM.SW[0], QM.B[1] - QM.SW[1]);
const CANOPY = { A: [40, -7], B: [163, 9], wall: [40, 0.26], h: 5.2, hWall: 6.4 };
const DOME = { x: 98, y: 16, drum: 16.5, dome: 20, lantern: 22, rx: 10.5 };
const QGRAN = ['#3a3634', '#4a4440', '#6a6258', '#8a8070', '#a89c88'];
const qGranite = (v, c, row) => mixHex(pick(QGRAN, v + (hash(c, row, 82) - 0.5) * 0.12, c, row), '#2a2438', 0.35);
function paintQuincy(p) {
  // the octagonal drum with its lit windows, the low copper dome (21 m across as seen), the lantern: first, so that the
  // hall's near cornice hides its foot
  const [cx0, , d] = proj(DOME.x, DOME.y), cx = Math.round(cx0), k = SV * DEG / d, rowAt = z => Math.round(HZ - SV * Math.atan2(z - EYE, d) * DEG);
  const rRoof = rowAt(QM.corn + 2.4), rDrum = rowAt(14.4), rDome = rowAt(19.6), rLan = rowAt(21.6), hw = Math.round(DOME.rx * k * 0.85);
  for (let y = rDrum; y <= rRoof; y++) for (let x = cx - hw + 1; x <= cx + hw - 1; x++) {
    const win = y === rDrum + 2 && (x - cx + 30) % 3 === 1 && Math.abs(x - cx) < hw - 2;
    p.set(x, y, win ? '#c89058' : pick(['#3a3436', '#5a524c', '#7a7066', '#9a8e7c'], 0.62 - Math.abs(x - cx) / hw * 0.4 + (y === rDrum ? 0.3 : 0), x, y));
  }
  for (let y = rDome; y < rDrum; y++) {
    const u = (y - rDome + 0.5) / (rDrum - rDome), w = Math.round(hw * Math.sqrt(Math.max(0, 1 - (1 - u) * (1 - u))));
    for (let x = cx - w; x <= cx + w; x++) {
      const v = 0.28 + (cx - x) / hw * 0.18 + u * 0.22 + ((x - cx + 30) % 3 === 0 ? -0.07 : 0);
      p.set(x, y, pick(['#14221e', '#1e342c', '#2e4a3e', '#46665a', '#6a8a7a'], v, x, y, 0.6));   // weathered copper, lit from below
    }
  }
  for (let y = rLan; y < rDome; y++) for (let x = cx - 1; x <= cx; x++) p.set(x, y, x === cx - 1 ? '#d8c8a0' : '#8a8070');
  p.set(cx - 1, rLan - 1, '#e8d8a8');
  // the west end: the portico's corner column before the dark porch, the entablature and the foot of the pediment
  paintFace(p, QM.SW, QM.NW, u => QM.corn + (QM.apex - QM.corn) * (1 - Math.abs(2 * u - 1)), (s, z, d, c, row, top) => {
    if (top) return qGranite(0.95, c, row);
    if (z > QM.corn) return qGranite(z > (QM.corn + (QM.apex - QM.corn) * (1 - Math.abs(2 * s / 16 - 1))) - 0.45 ? 0.75 : 0.42, c, row);
    if (z > 8.4) return qGranite(z > 9.0 ? 0.85 : z > 8.75 ? 0.5 : 0.7, c, row);
    if (z < 0.6) return qGranite(0.5, c, row);
    const col = [[0, 1.2], [4.9, 6.1], [9.9, 11.1], [14.8, 16]].find(([a, b]) => s >= a && s < b);
    if (col) return qGranite(s - col[0] < 0.8 ? 0.9 : 0.62, c, row);                                // a column, floodlit from the plaza
    if (s > 6.6 && s < 9.4 && z < 3.6) return '#7a5434';                                             // the porch's lit door
    return mixHex(qGranite(0.3, c, row), '#6a4a30', z < 3 ? 0.3 : 0.1);                               // the porch wall in the columns' shade
  });
  // the south side: the west pavilion, the long middle, the east pavilion; seen almost edge-on, its windows show as a few
  // warm glints between the piers
  paintFace(p, QM.SW, QM.SE, flatTop(QM.corn), (s, z, d, c, row, top) => {
    if (top) return '#c8b898';
    const pav = s < QM_A || s > QM_B, edge = Math.abs(s - QM_A) < 0.8 || Math.abs(s - QM_B) < 0.8;
    if (z > 8.95) return qGranite(0.78, c, row);
    if (z > 8.7) return qGranite(0.32, c, row);                                                      // the cornice's shadow
    if (edge) return qGranite(0.72, c, row);                                                         // the pavilions' corner pilasters
    if (z > 4.9 && z < 5.4) return qGranite(0.66, c, row);                                           // the string course
    const bay = (s % 3) / 3, h = hash(Math.floor(s / 3), z > 5 ? 3 : 4, 81);
    if (z > 5.8 && z < 8.3 && bay > 0.3 && bay < 0.7) return h < (pav ? 0.4 : 0.3) ? mixHex('#e8b868', '#5a4a40', pav ? 0.45 : 0.6) : qGranite(0.3, c, row);
    if (z < 4.4 && z > 0.5 && bay > 0.25 && bay < 0.75) return h < 0.7 ? pick(['#4a3424', '#7a5232', '#a87444'], 0.3 + 0.6 * (1 - z / 4.4), c, row) : '#2a2228';   // shopfronts
    return qGranite(z < 0.5 ? 0.4 : 0.55, c, row);
  });
  // the canopy's glazed end, a lean-to rising to the wall, and its long front: glass on a lit frame, the shops and the
  // pushcarts glowing under it
  const glass = (s, z, c, row, top, roof, phase, lit) => {
    if (top) return '#d8e0e0';
    if (z > roof - 1) return hash(c, row, 83) < 0.5 ? '#8a9aa0' : '#6a7a84';
    if (z < 1.2) return '#3a2a24';
    if ((s % 1.8) < 0.18) return '#5a4636';                                                          // a glazing bar
    return pick(['#5a3a28', '#a86a38', '#e0a050', '#f8d088'], (0.6 + 0.4 * Math.sin(s * 0.9 + phase)) * 0.7 * lit + 0.2, c, row);
  };
  const endLen = Math.hypot(CANOPY.wall[0] - CANOPY.A[0], CANOPY.wall[1] - CANOPY.A[1]), endTop = u => lerp(CANOPY.h, CANOPY.hWall, u);
  paintFace(p, CANOPY.A, CANOPY.wall, endTop, (s, z, d, c, row, top) => glass(s, z, c, row, top, endTop(s / endLen), 2, 0.75));   // seen face-on, a step dimmer
  paintFace(p, CANOPY.A, CANOPY.B, flatTop(CANOPY.h), (s, z, d, c, row, top) => glass(s, z, c, row, top, CANOPY.h, 0, 1));
}
/* ================= Faneuil Hall ================= */
// The west gable front (seven bays) and the south side (nine), red brick in Flemish bond, floodlit from below: cream
// pilaster capitals, two entablatures and the cornice; round-arched windows with white sashes on all three floors of the
// front (rectangular with lunettes on the side's upper floors); the pediment's raking cornice, its lunette and two
// ox-eyes; a chimney behind the apex. Each face is mapped through the panorama (paintFace), so the bays foreshorten as
// they do from the square. Two canvases: the hall with its windows dark, and the windows lit (drawn per frame where
// the shops and, in the evening, the Great Hall on the floors above are lit).
const BRICK_LIT = ['#2a1210', '#42201a', '#5a2a20', '#743626', '#90442e', '#aa5436', '#c26840', '#d47e4c'];
const TRIM = ['#5a4a42', '#8a7664', '#b49c80', '#d4bc98', '#e8d4b0'];
const trim3 = (v, c, row) => pick(TRIM, v, c, row, 0.25);
const STOREYS = [{ z0: 0.7, z1: 3.9 }, { z0: 6.0, z1: 9.3 }, { z0: 11.2, z1: 14.3 }];
const BELTS = [[4.5, 5.15], [9.95, 10.55]];
const FACES = {
  west: { A: FH.NW, B: FH.SW, len: FH.WEST_W, bays: 7, gable: true, doors: [1, 3, 5], dim: 0 },
  south: { A: FH.SW, B: FH.SE, len: FH.SOUTH_W, bays: 9, gable: false, doors: [2, 6], dim: 0.12 },
};
const gableTop = u => FH.CORN + (FH.APEX - FH.CORN) * (1 - Math.abs(2 * u - 1));
const L3S = proj(-45, -4, 3.5);                     // the lamp in front of the west front, its light on the brick
const FH_WIN = [];                                  // the windows' boxes: { x0, y0, x1, y1, storey, face }
function faceMat(name, mode) {
  const F = FACES[name], pitch = F.len / F.bays, hw = 0.8;
  return (s, z, d, c, row, isTop) => {
    const kpx = SV * DEG / d, u = s / F.len, top = F.gable ? gableTop(u) : FH.CORN;
    const k = Math.min(F.bays - 1, Math.floor(s / pitch)), sk = (k + 0.5) * pitch, ds = Math.abs(s - sk);
    // the openings
    for (let j = 0; j < 3; j++) {
      const S = STOREYS[j], door = j === 0 && F.doors.includes(k), z0 = door ? 0 : S.z0, z1 = S.z1;
      const arch = name === 'west' || j === 0;
      if (z >= z0 - 0.26 && z < z0 && ds < hw + 0.16 && !door) return mode === 'lit' ? null : trim3(0.62, c, row);   // the sill
      if (z < z0 || z > z1 || ds > hw) continue;
      const az = z1 - hw, inArch = !arch || z <= az || Math.hypot(ds, z - az) <= hw;
      if (!inArch) continue;
      const frame = ds > hw - 0.55 / kpx || (arch && z > az && Math.hypot(ds, z - az) > hw - 0.55 / kpx) || (!arch && z > z1 - 0.5 / kpx);
      if (frame) return mode === 'lit' ? null : trim3(0.6 + 0.15 * Math.exp(-z / 6) - F.dim, c, row);
      const lun = !arch && z > z1 - 0.9;                                                   // the side's lunettes over the sashes
      if (mode === 'lit') {
        const bar = ds < 0.5 / kpx || ((z - z0) % 0.8) < 0.9 / kpx || lun;
        const g = (z - z0) / (z1 - z0);
        const ramp = j === 0 ? ['#9a7048', '#c89a68', '#e8c490', '#f8e4c0'] : ['#6a3a24', '#a06838', '#d09450', '#f0c47a'];
        const col = pick(ramp, 0.25 + 0.55 * (1 - Math.abs(g - 0.55)) + (j ? 0.1 * Math.sin(ds * 9) : 0), c, row);
        const box = FH_WIN.find(w => w.face === name && w.k === k && w.storey === j);
        if (box) { box.x0 = Math.min(box.x0, c); box.x1 = Math.max(box.x1, c); box.y0 = Math.min(box.y0, row); box.y1 = Math.max(box.y1, row); }
        else FH_WIN.push({ face: name, k, storey: j, x0: c, x1: c, y0: row, y1: row });
        return bar ? mixHex(col, '#3a2010', 0.55) : col;
      }
      const refl = 0.3 + 0.25 * (1 - (z - z0) / (z1 - z0));
      return pick(['#0e0c16', '#16141e', '#201e2a', '#2c2a36'], refl + (ds < 0.5 / kpx ? 0.35 : 0), c, row);
    }
    if (mode === 'lit') return null;
    // the pediment: raking cornice, tympanum, lunette, ox-eyes
    if (F.gable && z > FH.CORN) {
      if (isTop || z > top - 0.45) return trim3(0.78 - (z - FH.CORN) * 0.03, c, row);
      if (z > top - 0.7) return '#2a1410';
      const lr = Math.hypot(s - F.len / 2, z - (FH.CORN + 0.05));
      if (lr < 4.1) {
        if (lr > 3.55) return trim3(0.7, c, row);
        const a = Math.atan2(z - FH.CORN, s - F.len / 2), fan = frac(a / (Math.PI / 6)) < 0.14 || Math.abs(lr - 1.6) < 0.5 / kpx;
        return fan ? '#9a8c7c' : pick(['#1a1a2a', '#24243a', '#323248', '#44445a'], 0.35 + 0.45 * (1 - lr / 3.6), c, row);
      }
      for (const ox of [F.len * 0.255, F.len * 0.745]) {
        const orr = Math.hypot(s - ox, z - 16.95);
        if (orr < 0.72) return orr > 0.42 ? trim3(0.72, c, row) : '#1e1e2e';
      }
    }
    // the entablatures and the main cornice
    if (z > 15.05 && z <= FH.CORN + 0.02) return trim3((z > 15.5 ? 0.8 : 0.62) - F.dim, c, row);
    if (z > 14.88 && z <= 15.05) return '#3a1a14';
    for (const [b0, b1] of BELTS) {
      if (z >= b0 && z <= b1) return trim3((z > b1 - 0.25 ? 0.76 : 0.6) + 0.08 * Math.exp(-z / 6) - F.dim, c, row);
      if (z >= b0 - 0.16 && z < b0) return '#341812';
    }
    // pilasters at the bay lines, with cream capitals under each entablature
    const pl = Math.abs(s - Math.round(s / pitch) * pitch), pil = pl < 0.3;
    if (pil && [[4.25, 4.5], [9.7, 9.95], [14.62, 14.88]].some(([a, b]) => z >= a && z < b)) return trim3(0.66 - F.dim, c, row);
    // the brick, floodlit from its foot, and warmed by the lamp in front
    let L = 0.5 + 0.26 * Math.exp(-z / 5.5) + 0.1 * Math.exp(-z / 1.3) - (z > FH.CORN ? 0.1 : 0) + (pil ? 0.07 : 0) - F.dim;
    L += 0.16 * Math.exp(-(((c - L3S[0]) / 14) ** 2) - (((row - L3S[1]) / 22) ** 2));
    if (s < 0.12 * 1 || (name === 'west' && s > F.len - 0.6 / kpx)) L += 0.12;                     // the corners catch the light
    L += (vnoise(s * 3, z * 2.5, name === 'west' ? 5 : 6) - 0.5) * 0.12 + ((Math.floor(z * 4.3) & 1) ? 0.02 : -0.02);
    return pick(BRICK_LIT, L, c, row, 0.8);
  };
}
// The cupola at the east end: a quoined square base, the octagonal belfry with its round arches (the 1867 bell inside),
// a cornice, the lead dome and the finial; the grasshopper is drawn per frame (it turns). Floodlit warm white.
const CUPC = Math.round(proj(CUP.x, CUP.y)[0]);
const cupRow = z => Math.round(HZ - SV * Math.atan2(z - EYE, dist(CUP.x, CUP.y)) * DEG);
const CUPR = { ridge: cupRow(FH.APEX), base: cupRow(26), belfry: cupRow(29.9), dome: cupRow(31.9), rod: cupRow(33.2), vane: cupRow(34.5) };
const CUP_W = ['#6a5e58', '#a89888', '#d8c8b0', '#f4e8d4', '#fffaf0'];
function paintCupola(p) {
  const cx = CUPC, { ridge, base, belfry, dome } = CUPR;
  for (let y = base; y <= ridge; y++) for (let x = cx - 6; x <= cx + 5; x++) {                      // the base (mostly behind the gable)
    const quoin = (x <= cx - 5 || x >= cx + 4) && ((y - base) % 3 === 0);
    p.set(x, y, pick(CUP_W, (quoin ? 0.55 : 0.82) - (x > cx ? 0.12 : 0), x, y));
  }
  for (let x = cx - 7; x <= cx + 6; x++) { p.set(x, base, pick(CUP_W, 0.95, x, base)); p.set(x, base - 1, pick(CUP_W, 0.7, x, base)); }
  for (let y = belfry + 1; y < base - 1; y++) for (let x = cx - 5; x <= cx + 4; x++) {               // the belfry
    const side = x < cx - 2 || x > cx + 1, pil = x === cx - 5 || x === cx - 3 || x === cx + 2 || x === cx + 4;
    let v = side ? 0.62 : 0.85;
    if (x > cx + 1) v -= 0.14;
    const yy = y - belfry, open = !pil && yy >= 3 && yy <= base - belfry - 3;
    const archTop = yy === 3 && (x === cx - 2 || x === cx + 1 || side);
    if (open && !archTop) p.set(x, y, side ? '#2a2026' : (yy > base - belfry - 7 && x >= cx - 1 && x <= cx ? '#6a4a2a' : '#1a1418'));   // the bell
    else p.set(x, y, pick(CUP_W, v, x, y));
  }
  for (let x = cx - 6; x <= cx + 5; x++) { p.set(x, belfry, pick(CUP_W, 0.92, x, belfry)); p.set(x, belfry + 1, pick(CUP_W, 0.6, x, belfry)); }
  const DW = [9, 9, 8, 7, 5, 3];                                                                     // the dome's half-widths, foot up
  for (let i = 0; i < belfry - dome; i++) {
    const y = belfry - 1 - i, w = DW[Math.min(DW.length - 1, Math.round(i * 5 / Math.max(1, belfry - dome - 1)))] / 2;
    for (let x = Math.round(cx - w - 0.5); x <= Math.round(cx + w - 0.5); x++) p.set(x, y, pick(['#2a2c34', '#3e424c', '#5a606a', '#8a8c90'], 0.45 + (cx - x) * 0.06 + (i === 0 ? 0.35 : 0), x, y));
  }
  p.set(cx - 1, dome - 1, '#c8a050'); p.set(cx, dome - 1, '#e8c060'); p.set(cx - 1, dome - 2, '#a88030');   // the ball
  for (let y = CUPR.vane + 3; y < dome - 2; y++) p.set(cx, y, '#8a7448');                           // the rod
  for (let x = cx - 2; x <= cx + 2; x++) p.set(x, CUPR.rod, '#7a6640');                              // the compass arms
}
function renderHall(mode) {
  const p = pix(W, H);
  if (mode === 'dark') {
    paintCupola(p);
    const [chx, chy] = proj(-33, 1.7, 23.2);                                                         // the chimney behind the apex
    for (let y = Math.round(chy); y < Math.round(chy) + 10; y++) for (let x = Math.round(chx) - 2; x <= Math.round(chx) + 2; x++) p.set(x, y, y === Math.round(chy) ? '#6a5a50' : pick(BRICK_LIT, 0.4 - (x > chx ? 0.1 : 0), x, y));
  }
  paintFace(p, FACES.south.A, FACES.south.B, flatTop(FH.CORN), faceMat('south', mode));
  paintFace(p, FACES.west.A, FACES.west.B, gableTop, faceMat('west', mode));
  return p.done();
}
/* ================= Sam Adams ================= */
// Anne Whitney's bronze (1880): Samuel Adams standing, arms folded, in his long coat, facing west towards City Hall, so
// from here we see his front and left side; on a tall Quincy granite pedestal with a polished panel, on a low round
// platform people sit on. Uplights at its foot light the bronze from below.
const ST = (() => { const [c, , d] = proj(STATUE.x, STATUE.y); return { cx: Math.round(c), base: Math.round(feetRow(d)), k: SV * DEG / d }; })();
const BRONZE = ['#121410', '#1e2018', '#2c2e22', '#40402e', '#5a563c', '#7a704c', '#9a8c5e'];
const GRANITE = ['#24242c', '#34343c', '#46464e', '#5c5a60', '#76726e', '#928a80'];
function renderStatue() {
  const p = pix(80, 70), ox = 40, oy = 64;                         // local origin: the platform's front at the pedestal's axis
  const put = (x, y, col) => p.set(ox + x, oy + y, col);
  for (let y = -3; y <= 1; y++) {                                  // the round platform, edge-on
    const w = y < -1 ? 19 : 22;
    for (let x = -w; x <= w; x++) put(x, y, y === -3 ? pick(GRANITE, 0.62, x, y) : pick(GRANITE, 0.38 + (y === -2 ? 0.12 : 0) - Math.abs(x) * 0.006 + 0.25 * Math.exp(-((x / 6) ** 2)), ox + x, oy + y));
  }
  const ped = (x0, x1, y0, y1, v) => { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) put(x, y, pick(GRANITE, v + (x > 2 ? -0.12 : 0) + (y - y0) * 0.012 + (x === x0 ? 0.1 : 0), ox + x, oy + y)); };
  ped(-9, 8, -6, -4, 0.5); ped(-8, 7, -8, -7, 0.62);               // the steps
  ped(-6, 5, -24, -9, 0.48);                                       // the dado
  for (let y = -21; y <= -11; y++) for (let x = -4; x <= 3; x++) put(x, y, pick(['#1c1c24', '#2a2a32', '#4a4850'], 0.4 + (y + 21) * 0.03 + (x === -4 ? 0.3 : 0), ox + x, oy + y));   // the polished panel
  ped(-7, 6, -26, -25, 0.7); ped(-6, 5, -27, -27, 0.55); ped(-5, 4, -28, -28, 0.66);   // the cornice and the plinth
  // the figure, 2.4 m: legs, the long coat, the folded arms, the head bowed a little, lit from below on its front (left)
  const fig = [
    '....hhh...', '...hHHhh..', '...hSSsh..', '....sss...', '...ccccc..', '..cCCCccc.', '.cCaaaaacc', '.cCaAAaacc', '.cCCCCcccc',
    '.cCCCcccc.', '.cCCCcccc.', '.cCCccccc.', '..CCCcccc.', '..CCCcccc.', '..CCcccc..', '..CCcccc..', '..CCcccc..', '..CCcccc..',
    '..LL..ll..', '..LL..ll..', '.FFF..fff.',
  ];
  const key = { h: BRONZE[2], H: BRONZE[4], S: BRONZE[5], s: BRONZE[3], c: BRONZE[2], C: BRONZE[4], a: BRONZE[3], A: BRONZE[6], L: BRONZE[4], l: BRONZE[2], F: BRONZE[3], f: BRONZE[1] };
  const fh = fig.length, scale = Math.max(1, (2.4 * ST.k) / fh);
  for (let j = 0; j < fh; j++) for (let i = 0; i < fig[j].length; i++) {
    const col = key[fig[j][i]];
    if (!col) continue;
    const y0 = -29 - Math.round((fh - j) * scale), y1 = -29 - Math.round((fh - j - 1) * scale);
    for (let y = y0; y < y1; y++) put(i - 5, y, col);
  }
  return { c: p.done(), x: ST.cx - ox, y: ST.base - oy + 2 };
}

/* ================= The square: granite paving, the Freedom Trail's red line, the lamps' pools ================= */
const BOff = KN.map(([b, c]) => [c, b]);
function bearingOfCol(c) {
  for (let i = 1; i < BOff.length; i++) if (c <= BOff[i][0] || i === BOff.length - 1) { const [c0, b0] = BOff[i - 1], [c1, b1] = BOff[i]; return b0 + (b1 - b0) * (c - c0) / (c1 - c0); }
  return 0;
}
const LAMPS = [{ x: -67.5, y: -18.2, near: true }, { x: -45, y: -4 }, { x: -40, y: -14 }];
const PAVE = ['#14141a', '#1a1a22', '#22222a', '#2a2a32', '#34343c', '#3e3e46'];
const WARMG = ['#3a322e', '#524436', '#6c5a44', '#8a7254', '#a88a64', '#c4a276'];
const GROUNDR = ['#121218', '#18181f', '#1f1e25', '#28252a', '#342e2e', '#443a34', '#584a3c', '#6e5c48', '#887056', '#a08664'];
const REDB = ['#4a1814', '#6a221a', '#8a2e22', '#a83a2a', '#c04a34'];
function segDist(px, py, ax, ay, bx, by) {
  const vx = bx - ax, vy = by - ay, u = clamp(((px - ax) * vx + (py - ay) * vy) / (vx * vx + vy * vy), 0, 1);
  return Math.hypot(px - ax - u * vx, py - ay - u * vy);
}
function groundLight(x, y) {
  let s = 0;
  for (const L of LAMPS) s += (L.near ? 0.8 : 0.6) * Math.exp(-(((x - L.x) ** 2 + (y - L.y) ** 2) / 26));
  s += 0.5 * Math.exp(-(((x - STATUE.x) ** 2 + (y - STATUE.y) ** 2) / 10));                         // the statue's uplights
  const dw = segDist(x, y, ...FH.NW, ...FH.SW), dS = segDist(x, y, ...FH.SW, ...FH.SE);
  s += 0.55 * Math.exp(-Math.min(dw, dS) / 2.2);                                                    // the hall's floodlights
  s += 0.6 * Math.exp(-segDist(x, y, ...PAV.NW, ...PAV.W) / 2.6) + 0.3 * Math.exp(-segDist(x, y, ...PAV.NE, ...PAV.NW) / 2);   // the raw bar's windows
  s += 0.35 * Math.exp(-(((x - FLOWER.x) ** 2 + (y - FLOWER.y) ** 2) / 6));                         // the flower cart's bulbs
  return Math.min(1, s);
}
const AXIS = Math.atan2(4.5, 30.5);                                                                  // the hall's long axis, the slabs' lines
function renderGround() {
  const Y0 = HZ - 1, p = pix(W, H - Y0);
  for (let r = Y0; r < H; r++) {
    const e = (r + 0.5 - HZ) / SV / DEG, d = EYE / Math.tan(Math.max(e, 0.0012)), foot = 0.00218 * d * d;
    for (let c = 0; c < W; c++) {
      const b = bearingOfCol(c + 0.5) / DEG, x = VX + d * Math.sin(b), y = VY + d * Math.cos(b);
      const g = groundLight(x, y), far = smooth(40, 160, d);
      let tr = 99;
      for (let i = 1; i < TRAIL.length; i++) tr = Math.min(tr, segDist(x, y, ...TRAIL[i - 1], ...TRAIL[i]));
      let col;
      if (tr < 0.3 && d < 120) col = pick(REDB, 0.35 + g * 0.6 + (hash(Math.floor(x * 5), Math.floor(y * 5), 3) - 0.5) * 0.25, c, r);
      else if (tr < 0.42 && d < 30) col = pick(PAVE, 0.75 + g * 0.3, c, r);
      else {
        const a = x * Math.cos(AXIS) + y * Math.sin(AXIS), bb = -x * Math.sin(AXIS) + y * Math.cos(AXIS);
        const row = Math.floor(bb / 0.75), off = (row & 1) * 0.7;
        const joint = foot < 0.32 && (frac(bb / 0.75) < foot / 0.75 * 0.6 + 0.04 || frac((a + off) / 1.4) < 0.03 * d / 4);
        const tone = (hash(Math.floor((a + off) / 1.4), row, 9) - 0.5) * 0.12 * (1 - far);
        const v = 0.42 + tone + (vnoise(x / 3, y / 3, 11) - 0.5) * 0.1 - (joint ? 0.18 : 0);
        col = pick(GROUNDR, (v - 0.42) * 0.9 + 0.2 + g * 0.72 - far * 0.08, c, r, 0.55);
        if (far > 0) col = mixHex(col, '#2a2230', far * 0.4);
      }
      p.set(c, r - Y0, col);
    }
  }
  return { c: p.done(), x: 0, y: Y0 };
}
/* a round granite planter of petunias near the viewer, bottom left: a drum seen from above, its lip and its foot curving
   round in front, rounded at the sides, and its shadow on the paving where it stands */
function renderPlanter() {
  const p = pix(48, 19), cx = 23.5;
  for (let x = 0; x < 48; x++) {
    const ex = (x - cx) / 24, k = Math.sqrt(Math.max(0, 1 - ex * ex)), lip = Math.round(4 + 2.5 * k), foot = Math.round(12 + 2.5 * k);
    for (let y = 2; y <= foot; y++) {
      if (y < lip && y < Math.round(4 - 2.5 * k)) continue;                                        // above the back of the lip
      const v = y <= lip ? 0.62 : 0.42 - (y - lip) * 0.012 - Math.abs(ex) ** 3 * 0.25 - ex * 0.06;   // the lit lip, the drum
      p.set(x, y, pick(GRANITE, v + (hash(x, y, 31) - 0.5) * 0.08, x, y));
    }
    if (k > 0.2) for (let y = foot + 1; y <= foot + (k > 0.7 ? 2 : 1); y++) p.set(x, y, [8, 8, 14], 150);   // its shadow
  }
  for (let x = 3; x < 45; x++) p.set(x, 4, pick(['#0e1a10', '#16261a'], hash(x, 5, 33), x, 4));        // the soil
  return p.done();
}
function renderFlowers() {
  const p = pix(48, 16), cx = 24;
  for (let y = 0; y < 6; y++) for (let x = 3; x < 45; x++) {
    if (((x - cx) / 21) ** 2 + ((y - 4) / 4) ** 2 > 1) continue;
    const h = hash(x, y, 33);
    p.set(x, y, h < 0.22 ? '#c84a8a' : h < 0.36 ? '#e8e0f0' : h < 0.46 ? '#8a3aa0' : pick(['#0e1a10', '#16261a', '#203420'], h, x, y));
  }
  return p.done();
}

/* ================= Honey locusts in Dock Square, wrapped in string lights ================= */
const LOCUST = ['#070c08', '#0c140c', '#121c10', '#1a2616', '#24321c', '#304024', '#3e5030'];
const BARK = ['#0e0c0a', '#1a1612', '#28221a', '#3a3224', '#4e4430'];
const BULBS = [];                                   // { x, y, ph, k, sway, tree }
// a tree in screen space: trunk foot (x, y), trunk height and lean, crown blobs; bulbs along the trunk and limbs
function renderTree(seed, fx, fy, trunkH, lean, crown, spread, lit, tree) {
  const p = pix(W, H), r = rng(seed), limbs = [];
  for (let y = fy - trunkH; y <= fy; y++) {
    const u = (fy - y) / trunkH, w = Math.max(1, Math.round(lerp(spread * 0.11 + 1, spread * 0.06 + 1, u))), x0 = Math.round(fx + lean * u * u - w / 2);
    for (let x = x0; x < x0 + w; x++) p.set(x, y, pick(BARK, (x === x0 ? 0.6 : 0.3) + lit * (1 - u) * 0.3, x, y));
    if (y % 3 === 0) BULBS.push({ x: x0 + (y % 6 === 0 ? 0 : w - 1), y, ph: r(), k: 30 + Math.floor(r() * 50), sway: u * 0.2, tree });
  }
  const tx = fx + lean, ty = fy - trunkH, blobs = [];
  for (let i = 0; i < crown.n; i++) {
    const a = -Math.PI / 2 + (r() - 0.5) * 2.6, len = crown.r * (0.5 + r() * 0.6), ex = tx + Math.cos(a) * len * crown.ax, ey = ty + Math.sin(a) * len * 0.7;
    limbs.push([tx, ty, ex, ey]);
    blobs.push({ x: ex, y: ey, rr: crown.r * (0.35 + r() * 0.3) });
  }
  for (const [x0, y0, x1, y1] of limbs) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let k = 0; k <= n; k++) {
      const x = lerp(x0, x1, k / n), y = lerp(y0, y1, k / n);
      p.set(x, y, pick(BARK, 0.4, x, y));
      if (k % 3 === 1) BULBS.push({ x: Math.round(x), y: Math.round(y), ph: r(), k: 30 + Math.floor(r() * 50), sway: 0.25 + 0.75 * k / n, tree });
    }
  }
  let x0 = W, x1 = 0, y0 = H, y1 = 0;
  for (const b of blobs) { x0 = Math.min(x0, b.x - b.rr); x1 = Math.max(x1, b.x + b.rr); y0 = Math.min(y0, b.y - b.rr); y1 = Math.max(y1, b.y + b.rr); }
  for (let y = Math.max(0, Math.floor(y0)); y <= Math.min(H - 1, y1); y++) for (let x = Math.max(0, Math.floor(x0)); x <= Math.min(W - 1, x1); x++) {
    let best = 0, by = 0;
    for (const b of blobs) { const dd = 1 - Math.hypot(x - b.x, (y - b.y) * 1.25) / b.rr; if (dd > best) { best = dd; by = b.y; } }
    const n = vnoise(x / 2.2, y / 2.2, seed), n2 = vnoise(x / 6, y / 5, seed + 1), edge = best + (n - 0.5) * 0.55;
    if (edge < 0.1 || (n2 > 0.72 && best < 0.5 && n > 0.45)) continue;                          // the locust's lacy, open crown
    const under = clamp((y - by) / 10, -1, 1);
    const v = 0.3 + under * 0.12 * lit + (n - 0.5) * 0.5 + lit * 0.18 * (under > 0 ? 1 : 0) - (edge < 0.22 ? 0.1 : 0);
    p.set(x, y, pick(LOCUST, v, x, y, 0.8));
    if (hash(x, y, seed) < 0.011) BULBS.push({ x, y, ph: hash(x, y, seed + 2), k: 24 + Math.floor(hash(x, y, seed + 3) * 60), sway: 0.6 + 0.4 * (1 - best), tree });
  }
  return trim(p);
}
/* ================= Street furniture ================= */
// Boston's double acorn lamp: a fluted black post, a crossbar with two arms, frosted acorn globes, a finial.
// Drawn at its own scale (k px a metre); the anchor is the post's foot.
function renderLamp(k) {
  const hgt = Math.round(4.2 * k), arm = Math.max(2, Math.round(0.55 * k)), gw = Math.max(2, Math.round(0.34 * k)), gh = Math.max(3, Math.round(0.55 * k));
  const w = arm * 2 + gw + 4, hh = hgt + gh + 4, p = pix(w, hh), cx = w >> 1, K = '#0c0d12', KH = '#2a2c36', thick = k > 12 ? 2 : 1;
  for (let y = gh + 3; y < hh; y++) for (let i = 0; i < thick; i++) p.set(cx - i, y, i ? K : (y < hh - 4 ? KH : K));
  for (let y = hh - Math.max(2, Math.round(0.5 * k)); y < hh; y++) for (let x = cx - thick - 1; x <= cx + 1; x++) p.set(x, y, K);   // the base
  const by = gh + 3;
  for (let x = cx - arm; x <= cx + arm; x++) p.set(x, by, K);
  for (const sx of [cx - arm, cx + arm]) {
    for (let y = 0; y < gh; y++) for (let x = sx - (gw >> 1); x < sx - (gw >> 1) + gw; x++) {
      const u = y / gh, edge = x === sx - (gw >> 1) || x === sx - (gw >> 1) + gw - 1;
      if (u < 0.25 && edge) continue;                                                              // the acorn's shoulders
      p.set(x, by - gh + y, u < 0.12 ? '#fffcf0' : edge ? '#f0d8a0' : u < 0.6 ? '#fff4d8' : '#f8e0a8');
    }
    p.set(sx, by - gh - 1, K);
  }
  p.set(cx, by - 2, K); p.set(cx, by - 3, K);                                                       // the finial
  return { c: p.done(), ax: cx, ay: hh - 1, gy: by - (gh >> 1), arm, fin: by - 3 };
}
// the flower seller's pushcart: two spoked wheels, a slatted body, buckets of peonies, hydrangeas and sunflowers,
// a green-and-cream striped canopy with a line of bulbs
const CART = (() => { const [c, , d] = proj(FLOWER.x, FLOWER.y); return { cx: Math.round(c), base: Math.round(feetRow(d)), k: SV * DEG / d }; })();
function renderCart() {
  const k = CART.k, w = Math.round(1.7 * k), bh = Math.round(0.85 * k), ch = Math.round(2.35 * k), p = pix(w + 8, ch + 3), ox = 4, gy = ch + 1;
  for (let y = gy - bh; y < gy - Math.round(0.25 * k); y++) for (let x = ox; x < ox + w; x++) p.set(x, y, pick(['#2a1a10', '#4a3020', '#6a4a30', '#8a6440'], 0.4 + (y % 3 === 0 ? 0.2 : 0) + (x < ox + 2 ? 0.2 : 0), x, y));
  for (const wx of [ox + 3, ox + w - 4]) {                                                           // the wheels
    const rr = Math.max(2, Math.round(0.32 * k));
    for (let a = 0; a < 24; a++) p.set(wx + Math.round(Math.cos(a / 24 * TAU) * rr), gy - rr + Math.round(Math.sin(a / 24 * TAU) * rr), '#1a120c');
    p.set(wx, gy - rr, '#8a6a40');
  }
  for (let x = ox + 1; x < ox + w - 1; x++) {                                                        // the flowers
    const hgt = 2 + Math.floor(hash(x, 1, 41) * 4);
    for (let j = 0; j < hgt; j++) {
      const hsh = hash(x, j, 42), col = hsh < 0.3 ? '#e8789a' : hsh < 0.45 ? '#f4e8e8' : hsh < 0.6 ? '#7a8ad8' : hsh < 0.72 ? '#f0c030' : '#2a4a24';
      p.set(x, gy - bh - 1 - j, col);
    }
  }
  for (const px0 of [ox, ox + w - 1]) for (let y = gy - ch; y < gy - bh; y++) p.set(px0, y, '#3a2a1a');   // the canopy poles
  for (let x = ox - 2; x < ox + w + 2; x++) for (let y = gy - ch - 2; y < gy - ch + 2; y++) p.set(x, y, ((x >> 1) & 1) ? '#e8e2cc' : '#2e6a3a');
  return { c: p.done(), x: CART.cx - ox - (w >> 1), y: CART.base - gy, bulbs: Array.from({ length: Math.floor((w + 4) / 3) }, (_, i) => [CART.cx - (w >> 1) - 2 + i * 3, CART.base - ch + 2]) };
}

/* ================= Briny's raw bar, the one-storey pavilion close on the right ================= */
// Its west front faces Congress Street (and the viewer, 9 to 13 m off): a granite plinth, big lit windows between brick
// piers, a glass door, a green fascia, a cream parapet; its north side runs edge-on to the square. Diners show in the
// windows (per frame). The roof sign, its posts and lamps are the city's billboard.
const PAV_WIN = [];                                  // glass boxes of the west front: { x0, x1, y0, y1 }
const PAV_GLASS = [];                                // the west front's glass by column: [top row, bottom row]
const PAV_FASCIA = { x0: W, x1: 0, y0: H, y1: 0 };
function pavMat(face) {
  const shade = face === 'west' ? 0 : face === 'north' ? 0.12 : 0.22;
  return (s, z, d, c, row, isTop) => {
    if (isTop || z > 4.15) return pick(['#2a2428', '#3c3436', '#544a48'], (isTop ? 0.9 : 0.45) - shade, c, row, 0.4);
    if (z > 3.95) return '#2a201c';
    if (z > 3.25) {
      if (face === 'west') { PAV_FASCIA.x0 = Math.min(PAV_FASCIA.x0, c); PAV_FASCIA.x1 = Math.max(PAV_FASCIA.x1, c); PAV_FASCIA.y0 = Math.min(PAV_FASCIA.y0, row); PAV_FASCIA.y1 = Math.max(PAV_FASCIA.y1, row); }
      return pick(['#0c1e18', '#12281e', '#1a3426'], 0.5 - shade + (z > 3.85 ? 0.3 : 0), c, row);
    }
    if (z < 0.55) return pick(GRANITE, 0.3 - shade + (z > 0.45 ? 0.2 : 0), c, row);
    const bay = 1.45, sb = s % bay, pier = sb < 0.32, door = face === 'west' && s > 2.3 && s < 3.15;
    if (door) return z > 2.65 ? pick(BRICK_LIT, 0.35, c, row) : (s < 2.38 || s > 3.07 || z > 2.57 ? '#141014' : pick(['#6a4428', '#9a6a3c', '#c08a52'], 0.45 + 0.3 * (1 - z / 2.6), c, row));
    if (pier || z > 3.05) return pick(BRICK_LIT, 0.3 - shade + (pier && sb < 0.08 ? 0.1 : 0) + (hash(c, row, 55) - 0.5) * 0.1, c, row);
    if (z < 0.75) return '#1a1414';
    const v = 0.35 + 0.45 * smooth(0.75, 2.6, z) - shade * 1.5;
    if (face === 'west') {
      const i = Math.floor(s / bay), box = PAV_WIN[i] || (PAV_WIN[i] = { x0: W, x1: 0, y0: H, y1: 0 });
      box.x0 = Math.min(box.x0, c); box.x1 = Math.max(box.x1, c); box.y0 = Math.min(box.y0, row); box.y1 = Math.max(box.y1, row);
      const g = PAV_GLASS[c] || (PAV_GLASS[c] = [row, row]); g[0] = Math.min(g[0], row); g[1] = Math.max(g[1], row);   // the glass, the room's mask
    }
    if (z > 2.55 && z < 2.66) return '#e8c890';                                                       // the strip of lamps over the bar
    if (z > 2.66 && (Math.floor(z * 9) & 1)) return '#3a2216';                                     // the blinds, half down
    return pick(['#24160f', '#4a2c1a', '#7a4c2c', '#a8703c', '#c89058'], v * 0.85, c, row);
  };
}
function renderPavilion() {
  const p = pix(W, H);
  paintFace(p, PAV.NE, PAV.NW, flatTop(PAV.h), pavMat('north'));
  paintFace(p, PAV.W, PAV.S, flatTop(PAV.h), pavMat('south'));
  paintFace(p, PAV.NW, PAV.W, flatTop(PAV.h), pavMat('west'));
  return trim(p);
}
// the roof sign: a frame on two posts standing on the pavilion's north-west corner, two gooseneck lamps over it
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  const foot = c => { const u = uAtCol(PAV.NW, PAV.W, c + 0.5); return Math.round(proj(lerp(PAV.NW[0], PAV.W[0], u), lerp(PAV.NW[1], PAV.W[1], u), 4.2)[1]); };
  for (const lx of [BB.x + 7, BB.x + BB.w - 8]) for (let y = B; y <= foot(lx); y++) { p.set(lx, y, '#1a1c24'); p.set(lx + 1, y, '#0c0d12'); }   // bolted to the parapet
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) p.set(x, y, y === T0 || x === L ? '#4a4c56' : y === B || x === Rr ? '#101116' : '#22242c');
  for (const lx of [BB.x + 10, BB.x + BB.w - 11]) { p.line(lx, T0, lx, T0 - 4, '#2a2c36'); p.line(lx, T0 - 4, lx + 3, T0 - 5, '#2a2c36'); p.set(lx + 3, T0 - 4, '#3a3c48'); p.set(lx + 4, T0 - 4, '#3a3c48'); }
  return trim(p);
}
// Briny's: a half shell with its pearl on deep teal, the name in cream, OYSTERS and RAW BAR under it
const SHELL = ['....wwwww...', '..wwWWWWWww.', '.wWWwwwwwWWw', 'wWwwppwwwwWw', 'wWwpPPpwwwWw', 'wWwwppwwwwWw', '.wWwwwwwwWw.', '..wwWWWWww..', '....wwww....'];
const LETTERS = {
  B: ['110', '101', '110', '101', '110'], R: ['110', '101', '110', '101', '101'], I: ['111', '010', '010', '010', '111'], N: ['110', '101', '101', '101', '101'],
  Y: ['101', '101', '010', '010', '010'], "'": ['010', '010', '000', '000', '000'], S: ['011', '100', '010', '001', '110'], O: ['010', '101', '101', '101', '010'],
  T: ['111', '010', '010', '010', '010'], E: ['111', '100', '110', '100', '111'], A: ['010', '101', '111', '101', '101'], W: ['101', '101', '111', '111', '101'], ' ': ['000', '000', '000', '000', '000'],
};
// N, M and W drawn wide on the signs: at 1x FONT3's N reads as a Π and its M and W as an H
const WIDE = { N: ['1001', '1101', '1011', '1001', '1001'], M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'] };
const signGlyph = ch => WIDE[ch] ?? FONT3[ch];
const signW = s => [...s].reduce((w, ch) => w + (signGlyph(ch)?.[0].length ?? 3) + 1, 0) - 1;
// The fascia's lettering, centred along the west front on the band's mid-line (z 3.6 m, halfway between its edges): the
// front recedes to the left, so its edges slope, and each letter steps along with them, centred on the mid-line at its
// own middle column (letters kept whole: sheared 3 px glyphs break up).
function renderFasciaText() {
  const s = 'LOBSTER ROLLS  CHOWDER', w = signW(s), f = PAV_FASCIA, p = pix(W, H);
  const mid = c => { const u = uAtCol(PAV.NW, PAV.W, c + 0.5); return proj(lerp(PAV.NW[0], PAV.W[0], u), lerp(PAV.NW[1], PAV.W[1], u), 3.6)[1]; };
  let x = Math.round((f.x0 + f.x1 + 1 - w) / 2);
  for (const ch of s) {
    const g = signGlyph(ch), gw = g?.[0].length ?? 3, y = Math.round(mid(x + (gw - 1) / 2) - 2.5);
    if (g) g.forEach((row, j) => { for (let i = 0; i < gw; i++) if (row[i] === '1') p.set(x + i, y + j, '#c8b888'); });
    x += gw + 1;
  }
  return trim(p);
}
function inkTo(p, s, x, y, col) { for (const ch of s) { const g = WIDE[ch] ?? LETTERS[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(x + i, y + j, col); }); x += (g?.[0].length ?? 3) + 1; } }
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#0a2a2c', '#0e3634', '#14423e'], 0.7 - j * 0.03, i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#c8b890'); p.set(i, h - 1, '#c8b890'); }
  const key = { w: '#d8d0c0', W: '#a89c88', p: '#f4f0ff', P: '#ffffff' };
  SHELL.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(1 + i, 5 + j, key[row[i]]); });
  const right = s => 14 + Math.floor((w - 14 - signW(s)) / 2);                               // centred right of the shell
  inkTo(p, "BRINY'S", right("BRINY'S"), 2, '#f0e4c4');
  inkTo(p, 'OYSTERS', right('OYSTERS'), 8, '#e8a87a');
  inkTo(p, 'RAW BAR', right('RAW BAR'), 14, '#a8d0c4');
  return gradeAd(p.done(), { level: 0.74, tint: '#14182e', mix: 0.12, wear: 0.25, seed: 17 });
}
/* ================= People ================= */
// One function draws everyone (after rome.js and boston.js): a look gives the proportions for a height and the colours
// dimmed for the night; person() puts it on its feet. Early-June clothes: T-shirts, shorts, sundresses, a light jacket.
// Those facing the hall are lit on their fronts by its floodlit brick, so seen from behind their shoulders and hair
// catch a line of its warm light.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#c8c4c0'];
const NIGHT = '#0c1030', GLOWC = '#f0b888';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : h >= 12 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.35), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m(o.shoe || '#1a161c'), long: o.long || 0, coat: o.coat || 0, shorts: o.shorts || 0,
    hat: o.hat ? m(o.hat) : null, tricorn: o.tricorn || 0, bowler: o.bowler || 0, dress: o.dress || 0, apron: o.apron ? m(o.apron) : null,
    stock: o.stock ? m(o.stock) : null, stripe: o.stripe ? m(o.stripe) : null };
}
/** a look at any height, cached (for walkers who grow and shrink with distance) */
function lookAt(skin, hair, top, bot, o = {}) { const cache = {}; return h => cache[h] || (cache[h] = look(h, skin, hair, top, bot, o)); }
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (phone, cone), 3 out, 4 up and out (pointing); o.lean: the
 * head towards a neighbour; o.sit: seated (on the platform or the ground); o.lift: rows the whole figure is raised
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy - (o.lift || 0));
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? Math.max(2, tw - 2) : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1, low = P.shorts ? Math.ceil(legs * 0.55) : P.stock ? Math.ceil(legs * 0.5) : 0, legC = P.dress ? P.top : P.bot, lowC = P.stock || P.skin;
    if (!o.lift) { ctx.fillStyle = 'rgba(8,8,20,0.34)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); }
    const leg = (lx, ly0, n) => { R(lx, ly0, 1 + (side ? 1 : 0), n, legC); if (low) R(lx, fy - low + 1, 1 + (side ? 1 : 0), low - 1, lowC); };
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        leg(dir > 0 ? x + 1 : x - 2, ly + 2, legs - 2); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        leg(dir > 0 ? x - 2 : x + 1, ly + 2, legs - 3); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { leg(x - 1, ly, legs); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, legC); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      if (low) { R(tx, fy - low + 1, lw, low - 1, lowC); R(tx + bw - lw, fy - low + 1, lw, low - 1, lowC); }
      if (P.dress) R(tx - 1, ly, bw + 2, 3, P.top);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) { R(tx - 1, fy - 1, bw + 2, 2, P.bot); if (o.dangle) { R(tx, fy + 1, 2, 3, P.shorts ? P.skin : P.bot); R(tx + bw - 2, fy + 1, 2, 3, P.shorts ? P.skin : P.bot); } }
  R(tx, ty, bw, th + P.coat, P.top);
  if (P.stripe) for (let k = 1; k < th; k += 2) R(tx, ty + k, bw, 1, P.stripe);
  if (P.apron && view !== 0) R(tx + 1, ty + 2, bw - 2, th + P.coat - 2, P.apron);
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                            // the hall's glow on the shoulders
  const skinArm = !P.coat;
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0);
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.topD); R(x + dir * 4, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, P.topD); R(x + dir * 5, ty - 3, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); }
  if (P.bowler) { R(hx - 1, hy, hw + 2, 1, '#121216'); R(hx, hy - 2, hw, 2, '#18181e'); }
  if (P.tricorn) { R(hx - 1, hy - 1, hw + 2, 2, '#121014'); R(hx + (hw >> 1) - (side ? 0 : 1), hy - 2, side ? 1 : 2, 1, '#121014'); R(hx - 1, hy - 2, 1, 1, '#121014'); R(hx + hw, hy - 2, 1, 1, '#121014'); }
  return hy;
}
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
/** where someone at x, y (metres) stands on the screen: column, feet row, distance, height px for hM metres */
function spotAt(x, y, hM = 1.72) { const [c, , d] = proj(x, y); return { x: Math.round(c), fy: Math.round(feetRow(d)), d, h: Math.max(6, Math.round(tallPx(d, hM))) }; }
/* ================= The cast ================= */
const BUSK = spotAt(CIRCLE.x, CIRCLE.y, 1.8);
const L_BUSK = look(BUSK.h, 1, 0, '#c8343a', '#16161e', { stripe: '#e8e0d4', bowler: 1, dk: 0.1 });
const L_BUSK_AT = lookAt(1, 0, '#c8343a', '#16161e', { stripe: '#e8e0d4', bowler: 1, dk: 0.1 });
const KPX_B = SV * DEG / BUSK.d;                                                    // px a metre at the pitch
// the circle: who stands where (compass bearing from the busker), each turned to face him; two kids sit in front
const AUD = [
  { th: 205, L: [2, 0, '#3a7a5a', '#2a2a30', { shorts: 1 }], rank: 0.05 }, { th: 252, L: [1, 4, '#d88aa0', '#2a2a3a', { long: 2, dress: 1 }], rank: 0.12 },
  { th: 228, r: 2.1, sit: true, hM: 1.25, L: [0, 3, '#e86a4a', '#2a3a6a', { shorts: 1, dk: 0.22 }], rank: 0.2 },
  { th: 160, L: [0, 3, '#e8e4dc', '#3a4a6a', { long: 3 }], rank: 0.55 }, { th: 292, L: [3, 0, '#e8c040', '#1e2a4a', { shorts: 1, hat: '#1e2e5a' }], rank: 0, vol: true },
  { th: 330, L: [0, 2, '#4a6a9a', '#c8b898', { shorts: 1 }], rank: 0.36 }, { th: 22, L: [1, 1, '#8a4a7a', '#1e2030', { long: 3 }], rank: 0.62 },
  { th: 214, r: 2.3, sit: true, hM: 1.2, L: [2, 0, '#4ab0c8', '#2a2a38', { shorts: 1, dk: 0.22 }], rank: 0.65 },
  { th: 72, L: [2, 5, '#5a5a4a', '#2a2a30', { coat: 1 }], rank: 0.75 }, { th: 118, L: [3, 1, '#c8a040', '#2a2a38', { long: 2 }], rank: 0.9 },
].map(a => {
  const r = a.r ?? CIRCLE.r, x = CIRCLE.x + r * Math.sin(a.th / DEG), y = CIRCLE.y + r * Math.cos(a.th / DEG), s = spotAt(x, y, a.hM ?? 1.72);
  const fx = -Math.sin(a.th / DEG), fy = -Math.cos(a.th / DEG), vx = VX - x, vy = VY - y, vl = Math.hypot(vx, vy), dot = (fx * vx + fy * vy) / vl;
  const view = a.sit ? 0 : dot > 0.35 ? 1 : dot < -0.35 ? 0 : 2;
  return { ...a, ...s, P: look(s.h, ...a.L), view, dir: Math.sign(BUSK.x - s.x) || 1 };
});
const clapping = t => inAny(t, CLAPS);
const unicycleState = t => t < ACT.setup ? 'lying' : t < ACT.mount ? 'stand' : t < ACT.down ? 'ridden' : t < ACT.end ? 'stand' : 'lying';
function drawUnicycle(x, fy, mode, t) {
  const k = KPX_B, rr = Math.max(2, Math.round(0.3 * k)), seat = Math.round(2.05 * k);
  if (mode === 'lying') {                     // laid on its side on the cobbles beside him: the wheel flat at one end, the saddle at the other
    R(x + rr - 1, fy - 1, seat - rr - 2, 1, '#6a6e78'); R(x + rr - 1, fy, seat - rr - 2, 1, '#1a1a20');
    R(x + seat - 4, fy - 2, 3, 2, '#1e1e24'); dot(x + seat - 4, fy - 2, '#4a4a56');
    for (let a = 0; a < 16; a++) { const s = Math.sin(a / 16 * TAU); dot(x - 1 + Math.cos(a / 16 * TAU) * rr, fy - 1 + s * Math.max(1, rr * 0.4), s < 0 ? '#5a5c66' : '#2e3038'); }
    dot(x - 1, fy - 1, '#8a8e98');
    return;
  }
  const spin = step(t, 960) & 1, cy = fy - rr;
  for (let a = 0; a < 16; a++) dot(x + Math.cos(a / 16 * TAU) * rr, cy + Math.sin(a / 16 * TAU) * rr, '#16161a');
  dot(x + (spin ? 1 : -1), cy + (spin ? -1 : 1), '#6a6c76');
  R(x, fy - seat, 1, seat - rr, '#b8bcc8'); R(x + 1, fy - seat + 2, 1, seat - rr - 4, '#3a3c46');   // the frame and its chain
  R(x - 1, fy - seat - 1, 3, 1, '#1a1a1e');                                                       // the saddle
  ctx.fillStyle = 'rgba(8,8,20,0.34)'; ctx.fillRect(x - rr - 1, fy + 1, rr * 2 + 3, 1);
}
/** juggling: n objects in a cascade between hands at (hl, hr), peak `amp` rows over hy; balls or clubs */
function drawJuggle(n, hl, hr, hy, amp, t, clubs, period) {
  for (let i = 0; i < n; i++) {
    const u = frac(t / period + i / n), leg = u < 0.5 ? u * 2 : (u - 0.5) * 2, fromL = u < 0.5;
    const x = fromL ? lerp(hl, hr, leg) : lerp(hr, hl, leg), y = hy - Math.sin(Math.PI * leg) * amp;
    if (clubs) {
      const rot = step(t, 2880) + i & 3;
      const col = '#f0ece0', knob = ['#c83a3a', '#3a8ad8', '#e8c030', '#5ab05a', '#d06ad0'][i];
      if (rot === 0) { dot(x, y - 1, col); dot(x, y, col); dot(x, y + 1, knob); }
      else if (rot === 1) { dot(x - 1, y - 1, col); dot(x, y, col); dot(x + 1, y + 1, knob); }
      else if (rot === 2) { dot(x - 1, y, knob); dot(x, y, col); dot(x + 1, y, col); }
      else { dot(x + 1, y - 1, col); dot(x, y, col); dot(x - 1, y + 1, knob); }
    } else dot(x, y, ['#e84a3a', '#f0d040', '#4a9ae8'][i % 3]);
  }
}
function drawBusker(t, crowd) {
  const { x, fy } = BUSK, P = L_BUSK, k = KPX_B, uni = unicycleState(t);
  const hatWalk = inWin(t, ACT.hat, ACT.end);
  let bx = x, view = [1, 2, 1, 2][step(t, 12)], dir = step(t, 24) & 1 ? 1 : -1, lift = 0, aL = 0, aR = 0;
  if (hatWalk) {                                                                                  // round the front of the circle with his hat
    const u = (t - ACT.hat) / (ACT.end - ACT.hat), a = (150 + 180 * u) / DEG, s = spotAt(CIRCLE.x + 2.4 * Math.sin(a), CIRCLE.y + 2.4 * Math.cos(a), 1.8);
    bx = s.x; view = 2; dir = u < 0.5 ? -1 : 1; aR = 3;
    person(bx, s.fy, L_BUSK_AT(s.h), { view, dir, step: Math.floor(t * 3.2) & 1, aR });
    R(bx + dir * 4 - 1, tyOf(P, s.fy) + 2, 3, 2, '#2a1e18');                                       // the hat held out
    drawUnicycle(x + 6, fy, 'stand', t);
    return;
  }
  if (uni === 'ridden') {
    const m = t < ACT.up ? smooth(ACT.mount, ACT.up, t) : t >= ACT.down ? 1 - smooth(ACT.down, ACT.down + 3.5, t) : 1;
    lift = Math.round(m * 1.2 * k) + (t > ACT.up && t < ACT.down ? Math.round(wave(t, 160)) : 0);
    bx = x + Math.round((1 - m) * 6) + (t > ACT.up && t < ACT.down ? Math.round(wave(t, 48)) : 0);
    view = 1;
    drawUnicycle(bx, fy, 'ridden', t);
  } else drawUnicycle(x + 6, fy, uni, t);
  const tyy = tyOf(P, fy) - lift, hy = tyy - P.hh;
  const clap = clapping(t), fin = inWin(t, ACT.clap, ACT.down);
  if (fin) { aL = 1; aR = 1; }
  else if (uni === 'ridden' && t >= ACT.clubs3) {
    const n = t < ACT.clubs4 ? 3 : t < ACT.clubs5 ? 4 : 5, high = inWin(t, ACT.high, ACT.clap);
    aL = 3; aR = 3;
    drawJuggle(n, bx - 4, bx + 4, tyy + 1, (n === 5 ? 1.5 : 1.15) * k + 4, t, true, n === 5 ? 1.2 : 1.6);
    if (high) {                                                                                   // one club thrown high over the hall's ground floor
      const u = (t - ACT.high) / 3, y = tyy - Math.sin(Math.PI * u) * 3.6 * k;
      dot(bx + 1, y, '#f0ece0'); dot(bx + 1, y + 1, '#f0ece0'); dot(bx + 1, y + 2, '#d06ad0');
    }
  } else if (uni === 'stand' && t < ACT.mount) { aR = 3; view = 2; dir = 1; }
  else if (!fin) {                                                                                // the ground act: balls, patter, a high throw before each clap
    const talk = frac(t / 30) > 0.78;
    if (talk) { aR = frac(t / 2.5) < 0.5 ? 4 : 3; }
    else {
      aL = 3; aR = 3;
      drawJuggle(3, bx - 4, bx + 4, tyy + 1, 0.9 * k, t, false, 1.2);
      const hi = CLAPS.find(([a]) => inWin(t, a - 2, a));
      if (hi) { const u = (t - hi[0] + 2) / 2, y = tyy - Math.sin(Math.PI * u) * 2.6 * k; dot(bx, y, '#f0d040'); }
    }
    if (clap) { aL = 1; aR = 1; }
  }
  person(bx, fy, P, { view, dir, aL, aR, lift });
}
// the flower seller and her customers
const VEND = spotAt(FLOWER.x + 1.1, FLOWER.y - 0.6);
const L_VEND = look(VEND.h, 1, 4, '#e8dcc8', '#2a2a30', { long: 3, apron: '#2e6a3a', dk: 0.16 });
const BUYER = lookAt(3, 0, '#c86a3a', '#2a2a38', { long: 2, dress: 1 }), BUYER2 = lookAt(0, 2, '#3a5a8a', '#c8b898', { shorts: 1 });
function drawVendor(t) {
  const sale = SALE.find(s => inWin(t, s, s + 10)), give = sale && inWin(t, sale + 5, sale + 7);
  const tidy = frac(t / 9);
  person(VEND.x, VEND.fy, L_VEND, give ? { view: 2, dir: -1, aR: 3 } : sale ? { view: 2, dir: -1, aR: tidy < 0.5 ? 2 : 0 } : { view: tidy < 0.6 ? 0 : 1, aL: tidy < 0.3 ? 3 : 0, aR: tidy > 0.15 && tidy < 0.45 ? 3 : 0 });
  if (give) { const ty = tyOf(L_VEND, VEND.fy); dot(VEND.x - 5, ty + 1, '#f08aa8'); dot(VEND.x - 6, ty + 1, '#f4e8e8'); dot(VEND.x - 5, ty + 2, '#3a6a3a'); }
}
function drawBuyer(t) {
  SALE.forEach((s, i) => {
    if (!inWin(t, s - 5, s + 16)) return;
    const away = i ? 1 : -1, xs = along([[s - 5, away > 0 ? -12 : -14], [s, 0], [s + 10, 0], [s + 16, away > 0 ? -16 : -14]], t);
    const p = spotAt(FLOWER.x - 1.3 + xs.x * 0.1, FLOWER.y + 0.4 - xs.x * 0.12), L = i ? BUYER2 : BUYER;
    const holding = t > s + 6;
    person(p.x + Math.round(xs.x), p.fy, L(p.h), xs.moving ? { view: 2, dir: xs.x < 0 && t < s ? 1 : -1, step: Math.floor(xs.x / 3) & 1, aR: holding ? 3 : 0 } : { view: 2, dir: 1, aR: inWin(t, s + 5, s + 7) ? 3 : 0 });
    if (holding) { const q = tyOf(L(p.h), p.fy); dot(p.x + Math.round(xs.x) + (xs.moving ? -3 : 4), q + 1, '#f08aa8'); }
  });
}
// the kid who runs at the pigeons, and the parent watching; the pigeons go up onto Sam Adams and come down again
const PARENT = spotAt(-70, -10), KIDH = spotAt(-69.2, -10.7, 1.2);
const L_PARENT = look(PARENT.h, 2, 0, '#5a7a4a', '#2a2a30', { shorts: 1 }), L_KID = lookAt(2, 0, '#e8c030', '#2a3a6a', { shorts: 1, dk: 0.2 });
const PIG_GROUND = [[-66.2, -6.4], [-65.4, -7.2], [-66.9, -7.4], [-65.8, -5.6], [-67.4, -6.2]].map(([x, y]) => spotAt(x, y, 0.3));
const PIG_PERCH = [[0, -51], [-3, -45], [3, -46], [-6, -27], [5, -27]];             // on the statue: head, shoulders, the cornice (local)
const PIG_K = { g: '#6a6c78', G: '#8a8c98', n: '#4a7a6a', k: '#2a2a30', p: '#c86a5a' };
const PIG_SPR = [['.gG', 'nGgg', '.p.p'], ['.gG.', 'ngggk', '..p.']], PIG_FLY = [['g...g', '.ggg.', '..n..'], ['.....', 'ggggg', '..n..']];
const chaseOf = t => CHASE.find(c => inWin(t, c, c + PERCH + 3));
function drawKid(t) {
  const c = CHASE.find(s => inWin(t, s - 1, s + 14));
  person(PARENT.x, PARENT.fy, L_PARENT, { view: 0, aR: c && inWin(t, c + 4, c + 9) ? 2 : 0, lean: c ? 1 : 0 });
  let kx = KIDH.x + 3, kfy = KIDH.fy, o = { view: 0, aL: 0, aR: 0 }, hh = KIDH.h;
  if (c) {
    const run = along([[c - 1, 0], [c + 1.6, 1], [c + 9, 1], [c + 13, 0]], t), tgt = spotAt(-66.8, -7.9, 1.2);
    kx = Math.round(lerp(KIDH.x + 3, tgt.x, run.x)); kfy = Math.round(lerp(KIDH.fy, tgt.fy, run.x)); hh = Math.round(lerp(KIDH.h, tgt.h, run.x));
    if (run.moving) o = { view: 2, dir: t < c + 5 ? 1 : -1, step: Math.floor(t * 6) & 1, aL: 3, aR: 3 };
    else o = { view: 0, aL: 4, aR: inWin(t, c + 2, c + 5) ? 1 : 0 };                          // pointing up at the birds on the statue
  } else if (hash(step(t, 60), 5) < 0.3) o = { view: 1, aR: 3 };
  person(kx, kfy, L_KID(hh), o);
}
function drawPigeons(t) {
  const c = chaseOf(t);
  PIG_GROUND.forEach((g, i) => {
    let x = g.x, y = g.fy - 2, fly = false;
    if (c !== undefined) {
      const lx = ST.cx + PIG_PERCH[i][0], ly = ST.base + 2 + PIG_PERCH[i][1] - 2, up = smooth(c + 1.2, c + 3.4 + i * 0.25, t), down = smooth(c + PERCH, c + PERCH + 2.6, t);
      const u = up * (1 - down);
      fly = (up > 0 && up < 1) || (down > 0 && down < 1);
      x = Math.round(lerp(g.x, lx, u)); y = Math.round(lerp(g.fy - 2, ly, u) - Math.sin(Math.PI * u) * 6);
    }
    const spr = fly ? PIG_SPR_C.fly[step(t, 2400) & 1] : PIG_SPR_C.sit[(step(t, 160) + i) & 1 ? 1 : 0][(i & 1)];
    if (!fly && c === undefined && (step(t, 320) + i * 3) % 7 === 0) y += 1;                     // pecking
    ctx.drawImage(spr, x - 2, y - 1);
  });
}
// the couple with ice cream on the statue's platform, and someone on their phone at its other side
const L_ICE = [lookAt(1, 1, '#3a6a9a', '#c8c0a8', { shorts: 1 }), lookAt(0, 3, '#e87a8a', '#2a2a3a', { long: 3, dress: 1 })];
const ICE_SEAT = [spotAt(-65.9, -4.7), spotAt(-65.2, -5.0)], SITTER = spotAt(-62.0, -5.0);
const L_SITTER = look(SITTER.h, 3, 0, '#d8d8d0', '#2a3040', { dk: 0.26 });
function drawIceCouple(t) {
  if (t < ICE.t0 || t >= ICE.t1) return;
  ICE_SEAT.forEach((s, i) => {
    const walkIn = t < ICE.sit, walkOut = t >= ICE.rise, u = walkIn ? smooth(ICE.t0, ICE.sit, t) : walkOut ? 1 - smooth(ICE.rise, ICE.t1, t) : 1;
    if (walkIn || walkOut) {
      const p = spotAt(lerp(-79 + i * 0.6, -65.9 + i * 0.7, u), lerp(-13 + i * 0.4, -5.2 - i * 0.2, u));
      person(p.x, p.fy, L_ICE[i](p.h), { view: 2, dir: walkIn ? 1 : -1, step: Math.floor(t * 3.2 + i) & 1, aR: 2 });
      return;
    }
    const lick = frac(t / 7 + i * 0.4) < 0.25, lean = frac(t / 40 + 0.3) < 0.18 ? (i ? -1 : 1) : 0;
    person(s.x, s.fy - 2, L_ICE[i](s.h), { view: 1, sit: true, dangle: true, aR: lick ? 2 : 3, aL: 0, lean });
    const ty = tyOf({ ll: 0, th: L_ICE[i](s.h).th }, s.fy - 2);
    dot(s.x + (lick ? 1 : 4), ty + (lick ? -2 : 1), i ? '#f0b0c0' : '#f4e8c8'); dot(s.x + (lick ? 1 : 4), ty + (lick ? -1 : 2), '#c89050');   // the cones
  });
}
function drawSitter(t) {
  person(SITTER.x, SITTER.fy - 2, L_SITTER, { view: 1, sit: true, dangle: true, aR: 2, aL: 2 });
  const ty = tyOf({ ll: 0, th: L_SITTER.th }, SITTER.fy - 2);
  R(SITTER.x - 1, ty - 1, 3, 2, '#9ad0ff'); halo(HL.screen, SITTER.x, ty - 3, 0.5 + 0.2 * wave(t, 24));
}
// someone photographing the hall, lowering the camera now and then to look
const PHOTO = spotAt(-56, -20.5), L_PHOTO = look(PHOTO.h, 2, 1, '#2a2a30', '#5a5a6a', { hat: '#c8b898' });
function drawPhotographer(t) {
  const look2 = frac(t / 20 + 0.4) > 0.75;
  person(PHOTO.x, PHOTO.fy, L_PHOTO, look2 ? { view: 0, aR: 3 } : { view: 0, aL: 2, aR: 2 });
}
// the raw bar: a table on its patio with two diners, the waiter bringing oysters on ice every 40 s
const TABLE = spotAt(-46.5, -24.6), DINERS = [spotAt(-47.4, -24.3), spotAt(-45.6, -24.9)], WAITER0 = spotAt(-50, -26.4);
const L_DINE = [look(DINERS[0].h, 0, 4, '#8a3a5a', '#2a2a30', { long: 3 }), look(DINERS[1].h, 1, 0, '#e8e4dc', '#2a2a30')];
const L_WAIT = lookAt(2, 0, '#f0ece4', '#1a1a20', { apron: '#1a1a20', dk: 0.12 });
function drawPatio(t, crowd) {
  const { x, fy } = TABLE;
  if (here(RANK.diners, crowd)) DINERS.forEach((d, i) => person(d.x, d.fy - 1, L_DINE[i], { view: 2, dir: i ? -1 : 1, sit: true, aR: frac(t / 11 + i * 0.5) < 0.2 ? 2 : 3, lean: frac(t / 30 + i) < 0.1 ? (i ? -1 : 1) : 0 }));
  R(x - 3, fy - 4, 7, 1, '#d8d0c0'); R(x, fy - 3, 1, 3, '#2a2a30');                              // the table
  dot(x, fy - 5, '#ffd890'); halo(HL.tiny, x, fy - 5, 0.6 + 0.15 * Math.sin(t * 7.3));
  if (frac(t / 40) < 0.4 && here(RANK.diners, crowd)) R(x - 2, fy - 5, 3, 1, '#c8d0d8');                // the plate of oysters on ice
  if (!here(RANK.waiter, crowd)) return;
  const u = frac(t / 40), go = u < 0.12 ? u / 0.12 : u < 0.2 ? 1 : u < 0.32 ? 1 - (u - 0.2) / 0.12 : 0;
  const wx = Math.round(lerp(WAITER0.x, x + 4, go)), wfy = Math.round(lerp(WAITER0.fy, fy, go)), wh = Math.round(lerp(WAITER0.h, TABLE.h, go));
  if (u < 0.34) {
    const moving = (u < 0.12 || (u > 0.2 && u < 0.32));
    person(wx, wfy, L_WAIT(wh), { view: moving ? 2 : 1, dir: u < 0.15 ? -1 : 1, step: moving ? Math.floor(t * 3.2) & 1 : 0, aR: u < 0.13 ? 3 : 0 });
    if (u < 0.13) R(wx - 5, tyOf(L_WAIT(wh), wfy) + 1, 3, 1, '#c8d0d8');
  }
}
// Inside Briny's, through the west front's glass (the room seen at a slant, so a table 2 m in sits well to the right of
// the window it shows in): a two-top in each big window under its pendant lamp, a couple in profile across it, forks
// and glasses going up now and then, heads leaning in; a regular alone with a pint by the door; and the waiter, back
// from the patio, crossing behind them to the near table and back. All warm-lit and clipped to the glass.
const PAV_DIR = (() => { const dx = PAV.W[0] - PAV.NW[0], dy = PAV.W[1] - PAV.NW[1], l = Math.hypot(dx, dy); return [dx / l, dy / l]; })();
/** the point dep metres inside the west front that shows at column c */
function inCol(c, dep) {
  const [ux, uy] = PAV_DIR, at = s => [PAV.NW[0] + ux * s - uy * dep, PAV.NW[1] + uy * s + ux * dep];
  let lo = -8, hi = 10;
  for (let k = 0; k < 30; k++) { const m = (lo + hi) / 2; if (colOf(bearing(...at(m))) < c) lo = m; else hi = m; }
  return at((lo + hi) / 2);
}
function barSpot(c, dep, hM = 1.72) {
  const d = dist(...inCol(c, dep)), row = z => Math.round(HZ - SV * Math.atan2(z - EYE, d) * DEG);
  return { x: c, d, row, h: Math.round(tallPx(d, hM)) };
}
const INSIDE = '#6a3e22';
function lookIn(h, skin, hair, top, bot, o = {}) {
  const P = look(h, skin, hair, top, bot, { ...o, dk: 0 });
  for (const k of ['skin', 'hair', 'hairL', 'top', 'topD', 'topL', 'bot', 'shoe', 'hat', 'apron']) if (P[k]) P[k] = mixHex(P[k], INSIDE, 0.22);
  return P;
}
// c: the table's column, dep: metres in, gap: px from the table to each diner; who: [skin, hair, top, bottom, options, height m]
const BAR = [
  { c: 351, dep: 2.6, gap: 5, rank: 0.35, who: [[0, 1, '#2a4a7a', '#2a2a30', {}, 1.8], [1, 3, '#c8604a', '#2a2a30', { long: 3 }, 1.66]], P: [12, 16], ph: [0.1, 0.55] },
  { c: 368, dep: 1.4, gap: 0, rank: 0.42, who: [[2, 0, '#5a5c66', '#2a2a30', {}, 1.78]], P: [20], ph: [0.3] },
  { c: 403, dep: 2.4, gap: 7, rank: 0.2, who: [[0, 5, '#e0d8c4', '#3a3a48', {}, 1.76], [3, 0, '#2a7a7a', '#2a2a30', { long: 4 }, 1.64]], P: [15, 10], ph: [0.7, 0.2] },
  { c: 433, dep: 1.5, gap: 7, rank: 0.05, who: [[1, 0, '#7a2a3a', '#2a2a30', { long: 4 }, 1.68], [0, 2, '#5a6a3a', '#2a2a30', {}, 1.82]], P: [16, 12], ph: [0.45, 0.85] },
].map(b => ({ ...b, T: barSpot(b.c, b.dep), seats: b.who.map((w, i) => {
  const x = b.gap ? b.c + (i ? b.gap : -b.gap) : b.c + 3, s = barSpot(x, b.dep, w[5]);
  return { ...s, P: lookIn(s.h, ...w.slice(0, 5)), dir: b.gap ? (i ? -1 : 1) : -1 };
}) }));
const L_WAIT_IN = h => lookIn(h, 2, 0, '#f0ece4', '#1a1a20', { apron: '#1a1a20' });
const WAIT_DEP = 3.4, WAIT_C = [336, 429];
/** the waiter inside: u = frac(t / 40) runs with the patio waiter's, who is out for u < 0.34 */
function waiterIn(t) {
  const u = frac(t / 40);
  if (u < 0.36 || u >= 0.8) return null;
  if (u < 0.52) return { c: lerp(WAIT_C[0], WAIT_C[1], (u - 0.36) / 0.16), dir: 1, moving: true, tray: true };
  if (u < 0.62) return { c: WAIT_C[1], dir: 1, moving: false, tray: u < 0.56 };
  return { c: lerp(WAIT_C[1], WAIT_C[0], (u - 0.62) / 0.18), dir: -1, moving: true, tray: false };
}
let PAV_MASK = null;
function drawBarInside(t, crowd) {
  if (!PAV_MASK) { PAV_MASK = new Path2D(); PAV_GLASS.forEach((g, c) => { if (g) PAV_MASK.rect(c, g[0], 1, g[1] - g[0] + 1); }); }
  ctx.save(); ctx.clip(PAV_MASK);
  const w = here(RANK.waiter, crowd) && waiterIn(t);
  if (w) {                                                                                         // behind the tables
    const s = barSpot(Math.round(w.c), WAIT_DEP, 1.76), fy = s.row(0), P = L_WAIT_IN(s.h);
    person(s.x, fy, P, { view: 2, dir: w.dir, step: w.moving ? Math.floor(t * 3.2) & 1 : 0, aR: w.tray ? 3 : 0 });
    if (w.tray) R(s.x + w.dir * 3 - 1, tyOf(P, fy) + 1, 3, 1, '#d8dce0');
  }
  BAR.forEach((b, bi) => {
    const { T } = b, tr = T.row(0.76), lamp = T.row(1.85), lit = 0.75 + 0.25 * wave(t, 6 + bi, bi * 0.3);
    R(b.c, T.row(2.5), 1, lamp - T.row(2.5), '#5a3420');                                           // the pendant's cord, its shade, the bulb
    const sw = b.dep < 2 ? 2 : 1;
    R(b.c - sw + 1, lamp, 2 * sw - 1, 1, '#4a3020'); R(b.c - sw, lamp + 1, 2 * sw + 1, 1, '#8a6a3a'); R(b.c - sw, lamp + 2, 2 * sw + 1, 1, '#fff0c0');
    halo(HL.tiny, b.c, lamp + 2, 0.7 * lit);
    const seated = here(b.rank, crowd);
    if (seated) b.seats.forEach((s, i) => {
      const e = frac(t / b.P[i] + b.ph[i]), talk = frac(t / 30 + b.ph[i] * 0.7) < 0.22;
      person(s.x, s.row(0.48), s.P, { view: 2, dir: s.dir, sit: true, aR: e < 0.14 ? 2 : e > 0.5 && e < 0.62 ? 3 : 0, lean: talk && b.gap ? s.dir : 0 });
    });
    const x0 = b.gap ? b.c - b.gap + 2 : b.c - 4, x1 = b.gap ? b.c + b.gap - 2 : b.c + 1;         // the table: its cloth and front edge
    R(x0, tr, x1 - x0 + 1, 1, '#e8dcc0'); R(x0, tr + 1, x1 - x0 + 1, 1, '#9a7a5a');
    if (b.gap) {
      dot(b.c, tr - 1, hash(bi, step(t, 1200)) < 0.7 ? '#fff0b0' : '#e8b060');                     // the candle, and two glasses when it's taken
      if (seated) { dot(x0 + 1, tr - 1, '#e8e4f0'); dot(x1 - 1, tr - 1, '#b8404a'); }
    } else if (seated) { R(x0 + 1, tr - 2, 1, 2, '#e0a040'); dot(x0 + 1, tr - 3, '#f4ecd8'); }      // the regular's pint
  });
  ctx.restore();
}
// the Freedom Trail tour: the guide in colonial dress (tricorn, claret frock coat, white stockings) with a lantern, five
// visitors behind him along the red line; they stop by the hall, he raises the lantern to the grasshopper, they go on
const TW = [[-77, -21], [-39, -16], [2, -11], [30, -8]];
const TWL = TW.slice(1).map((p, i) => Math.hypot(p[0] - TW[i][0], p[1] - TW[i][1]));
function trailAt(m, lat = 0) {
  let i = 0;
  while (i < TWL.length - 1 && m > TWL[i]) { m -= TWL[i]; i++; }
  const [ax, ay] = TW[i], [bx, by] = TW[i + 1], u = m / TWL[i], dx = (bx - ax) / TWL[i], dy = (by - ay) / TWL[i];
  return [ax + (bx - ax) * u - dy * lat, ay + (by - ay) * u + dx * lat];
}
const L_GUIDE = lookAt(1, 5, '#8a2030', '#1e1a20', { tricorn: 1, coat: 2, stock: '#e8e4dc', dk: 0.22 });
const L_TOUR = [lookAt(0, 3, '#3a8a9a', '#2a2a38', { long: 2 }), lookAt(2, 0, '#d8c050', '#3a3a48', { shorts: 1, hat: '#a83030' }),
  lookAt(1, 2, '#e8e4dc', '#4a4a5a', { shorts: 1 }), lookAt(3, 1, '#c85a8a', '#2a2a30', { long: 3, dress: 1 }), lookAt(0, 5, '#4a5a7a', '#c8b898', { coat: 1 })];
const tourM = t => along([[TOUR.t0, 2.5], [TOUR.stop, 29.5], [TOUR.go, 29.5], [TOUR.t1, 86]], t);
function drawTour(t, crowd) {
  if (t < TOUR.t0 || t >= TOUR.t1) return;
  const g = tourM(t), stopped = !g.moving, fade = 1 - smooth(TOUR.t1 - 5, TOUR.t1, t);
  ctx.globalAlpha = fade;
  const items = [];
  L_TOUR.forEach((L, i) => {
    if (!here(RANK.tour[i], crowd)) return;
    const m = stopped ? g.x - 1.6 - 0.75 * i : g.x - 1.7 * (i + 1), lat = stopped ? [-1.1, 0.9, -0.2, 1.6, -1.8][i] : (i & 1 ? 0.5 : -0.5);
    items.push({ i, p: spotAt(...trailAt(Math.max(0, m), lat), 1.72), L });
  });
  const gp = spotAt(...trailAt(g.x, 0), 1.75);
  items.push({ i: -1, p: gp, L: L_GUIDE });
  items.sort((a, b) => a.p.fy - b.p.fy);
  for (const it of items) {
    const P = it.L(it.p.h), x = it.p.x, fy = it.p.fy;
    if (it.i < 0) {
      const point = inWin(t, TOUR.point, TOUR.go - 1);
      const o = stopped ? { view: 1, aR: 3, aL: point ? 4 : 0 } : { view: 2, dir: 1, step: Math.floor(t * 3.2) & 1, aR: 3 };
      person(x, fy, P, o);
      const ty = tyOf(P, fy), lx = x + 4, ly = ty + 3;
      R(lx, ly, 2, 2, '#ffd888'); dot(lx, ly - 1, '#2a2420'); halo(HL.lantern, lx + 1, ly + 1, 0.85 * fade);
    } else {
      const look2 = stopped && t > TOUR.point + 1.5, phone = look2 && (it.i === 1 || it.i === 3) && t < TOUR.go - 2;
      person(x, fy, P, stopped ? { view: 0, aR: phone ? 2 : 0, aL: phone ? 2 : 0, lean: look2 ? 0 : (it.i & 1 ? 1 : -1) } : { view: 2, dir: 1, step: (Math.floor(t * 3.2) + it.i) & 1 });
      if (phone) { const ty = tyOf(P, fy) - P.hh - 2; R(x - 1, ty, 3, 2, '#9ad0ff'); halo(HL.screen, x, ty + 1, 0.6 * fade); }
    }
  }
  ctx.globalAlpha = 1;
}
// walkers: near the viewer across the square (lane A), and behind the circle in front of the hall (lane B, couples)
const LANE_A = [[-73, -12.5], [-58.5, -27]], LANE_B = [[-61, 10], [-38, -9.5]], LANE_P = [[-59, 15], [-34, -6]];
const L_WA = [lookAt(1, 0, '#e8e050', '#1a1c28', { shorts: 1 }), lookAt(2, 4, '#8a5a9a', '#1e2030', { long: 3 }), lookAt(0, 1, '#4a6a8a', '#c8b898', { shorts: 1, hat: '#c8323a' })];
const L_WB = [[lookAt(3, 0, '#e8e4dc', '#3a4a6a'), lookAt(1, 3, '#4a8a7a', '#2a2a38', { long: 3, dress: 1 })], [lookAt(0, 2, '#c84a3a', '#1e2030', { shorts: 1 })],
  [lookAt(2, 1, '#2a3a5a', '#c8b898'), lookAt(0, 4, '#e8a0b0', '#2a2a3a', { long: 2 })], [lookAt(1, 5, '#6a6a5a', '#2a2a30', { coat: 1 })]];
function laneSpot(L, u) { return spotAt(lerp(L[0][0], L[1][0], u), lerp(L[0][1], L[1][1], u)); }
function drawWalkers(t, crowd, lane) {
  if (lane === 'A' && here(RANK.walkA, crowd)) WALK_A.forEach((w, i) => {
    if (!inWin(t, w.t0, w.t1)) return;
    const u = (t - w.t0) / (w.t1 - w.t0), s = laneSpot(LANE_A, w.dir > 0 ? lerp(-0.08, 1.12, u) : lerp(1.12, -0.08, u));
    person(s.x, s.fy, L_WA[i](s.h), { view: 2, dir: w.dir, step: Math.floor(t * 3.2) & 1, aR: i === 1 ? 2 : 0 });
    if (i === 1) { const ty = tyOf(L_WA[i](s.h), s.fy); dot(s.x + w.dir * 4, ty - 1, '#9ad0ff'); }
  });
  if (lane === 'B' && here(RANK.walkB, crowd)) WALK_B.forEach((w, i) => {
    if (!inWin(t, w.t0, w.t1)) return;
    const u = (t - w.t0) / (w.t1 - w.t0);
    L_WB[i].forEach((L, j) => {
      const s = laneSpot(LANE_B, (w.dir > 0 ? lerp(-0.05, 1.05, u) : lerp(1.05, -0.05, u)) - j * 0.035 * w.dir);
      person(s.x, s.fy + j, L(s.h), { view: 2, dir: w.dir, step: (Math.floor(t * 3.2) + j) & 1, aR: j === 0 && L_WB[i].length > 1 ? 3 : 0 });
    });
  });
}
// a pedicab: the rider pedalling, two passengers under the red canopy, its lamp lit
const L_PEDI = lookAt(2, 0, '#2a8a5a', '#1e2030', { shorts: 1, hat: '#e8e4dc' }), L_PASS = [lookAt(0, 3, '#e8e4dc', '#2a2a38', { long: 2 }), lookAt(1, 0, '#3a5a8a', '#2a2a38')];
function drawPedicab(t, crowd) {
  if (!here(RANK.pedi, crowd)) return;
  for (const P of PEDI) {
    if (!inWin(t, P.t0, P.t1)) continue;
    const u = (t - P.t0) / (P.t1 - P.t0), s = laneSpot(LANE_P, P.dir > 0 ? lerp(-0.1, 1.1, u) : lerp(1.1, -0.1, u)), k = SV * DEG / s.d, d = P.dir;
    const rr = Math.max(2, Math.round(0.33 * k)), spin = step(t, 960) & 1;
    for (const wx of [s.x + d * Math.round(0.9 * k), s.x - d * Math.round(0.7 * k)]) {
      for (let a = 0; a < 12; a++) dot(wx + Math.cos(a / 12 * TAU) * rr, s.fy - rr + Math.sin(a / 12 * TAU) * rr, '#16161a');
      dot(wx + (spin ? 1 : -1), s.fy - rr, '#6a6c76');
    }
    const cab = s.x - d * Math.round(0.6 * k), ch = Math.round(1.6 * k), cw = Math.round(0.8 * k);
    R(cab - (cw >> 1), s.fy - Math.round(0.9 * k), cw, Math.round(0.4 * k), '#5a1a1a');
    L_PASS.forEach((L, j) => person(cab - j * d, s.fy - Math.round(0.75 * k), L(Math.round(s.h * 0.8)), { view: 1, sit: true, lean: j ? d : 0 }));
    for (let x = cab - (cw >> 1) - 1; x <= cab + (cw >> 1); x++) R(x, s.fy - ch, 1, 2, '#a82a2a');   // the canopy
    R(cab - (cw >> 1) - 1, s.fy - ch, 1, ch - Math.round(0.6 * k), '#3a1a1a');
    person(s.x + d * Math.round(0.3 * k), s.fy - Math.round(0.5 * k), L_PEDI(s.h), { view: 2, dir: d, sit: true, aR: 3 });
    const lx = s.x + d * Math.round(1.1 * k);
    dot(lx, s.fy - Math.round(0.9 * k), '#fff4d0'); halo(HL.tiny, lx, s.fy - Math.round(0.9 * k), 0.9);
  }
}
// a herring gull on the near lamp's finial, turning its head; bats round the floodlit cupola; moths at the lamps
const GULL = [['..ww.', '.wwwy', 'gggw.', '.gg..', '.y.y.'], ['.ww..', 'ywww.', '.wggg', '..gg.', '.y.y.']];
const GULL_K = { w: '#e8e8e4', g: '#9aa0aa', y: '#d8b040' };
function drawBats(t) {
  for (let i = 0; i < 3; i++) {
    const x = CUPC + 15 * Math.sin(TAU * ((29 + i * 8) * t / LOOP + i * 0.31)) + 6 * Math.sin(TAU * (71 * t / LOOP + i)), y = CUPR.belfry - 2 + 9 * Math.sin(TAU * ((43 + i * 6) * t / LOOP + i * 0.17));
    const f = step(t, 2400 + i * 240) & 1;
    ctx.fillStyle = '#0a0a10';
    ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
    if (f) { ctx.fillRect(Math.round(x) - 1, Math.round(y) - 1, 1, 1); ctx.fillRect(Math.round(x) + 1, Math.round(y) - 1, 1, 1); }
    else { ctx.fillRect(Math.round(x) - 1, Math.round(y), 1, 1); ctx.fillRect(Math.round(x) + 1, Math.round(y), 1, 1); }
  }
}
function drawMoths(t, cx, cy, n, r0) {
  for (let k = 0; k < n; k++) {
    const a = TAU * ((36 + k * 12) * t / LOOP + k / n), rr = r0 + k * 1.5 + Math.sin(TAU * (7 * t / LOOP + k)) * 1.2;
    ctx.globalAlpha = 0.75; dot(cx + rr * Math.cos(a), cy + rr * 0.7 * Math.sin(a * 1.3), '#f0e6cc'); ctx.globalAlpha = 1;
  }
}
/* ================= Prerendered state ================= */
let HL, SKY_C, CLOUDS, FAR_C, QM_C, HALL_D, HALL_L, STATUE_C, GROUND_C, PLANTER_C, TREES, LAMP_N, LAMP_F, CART_C, PAV_C, BOARD_C,
  AD_C, PITCH, PIG_SPR_C, GULL_SPR, VIG_C, LAMP_POS, FLOWERS_C, FASCIA_TXT;
const SPLIT = {};
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [5, 6, 18], Math.round(q * 0.4 * 255));
  }
  return p.done();
}
// the low glass shop pavilion on North Street, behind the circle (an invented shop, no sign)
function paintGlassBox(p) {
  const A = [-40, 27], B = [-1, 37];
  paintFace(p, A, B, flatTop(5), (s, z, d, c, row, top) => {
    if (top || z > 4.6) return '#2a2c34';
    if (z < 0.4) return '#1a1a20';
    const mull = (s % 2.4) < 0.25;
    return mull ? '#18191e' : pick(['#20262e', '#323c46', '#4a5662', '#6a7880', '#a8b4b8'], 0.3 + 0.25 * (1 - z / 4.6) + (hash(Math.floor(s / 2.4), 1, 91) - 0.5) * 0.4, c, row);
  });
}
function prerender() {
  WINS.length = 0; BULBS.length = 0; FH_WIN.length = 0; PAV_WIN.length = 0; PAV_GLASS.length = 0;
  Object.assign(PAV_FASCIA, { x0: W, x1: 0, y0: H, y1: 0 });
  HL = {
    lampN: makeHalo(34, '#ffc878', 0.15, 1.7), globeN: makeHalo(8, '#fff0d0', 0.4, 1.3), lampF: makeHalo(14, '#ffc878', 0.18, 1.6), globeF: makeHalo(4, '#fff0d0', 0.4, 1.2),
    cupola: makeHalo(18, '#fff0d8', 0.22, 1.4), flood: makeHalo(12, '#ffb070', 0.2, 1.5), up: makeHalo(10, '#ffd8a0', 0.3, 1.4), screen: makeHalo(4, '#9ad0ff', 0.4, 1.2),
    tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), lantern: makeHalo(6, '#ffc870', 0.5, 1.2), bulb: makeHalo(2, '#fff0c0', 0.5, 1), sign: makeHalo(22, '#ffe2b0', 0.14, 1.5),
    vista: makeHalo(36, '#ffb868', 0.12, 1.5), landing: makeHalo(7, '#f4f6ff', 0.45, 1.3), ch: makeHalo(12, '#f8e8c8', 0.18, 1.4), win: makeHalo(40, '#ffb060', 0.1, 1.6),
    gold: makeHalo(4, '#ffd860', 0.55, 1.2), cart: makeHalo(10, '#ffd890', 0.16, 1.4), pool: makeHalo(10, '#f0b070', 0.3, 1.1),
  };
  SKY_C = renderSky();
  CLOUDS = [makeCloud(11, 64, 7, ['#1a2046', '#242a54', '#323462', '#46426c', '#5a4e72'], 170), makeCloud(29, 70, 8, ['#20264e', '#2c305a', '#3c3a66', '#524870', '#685674'], 185),
    makeCloud(47, 72, 8, ['#2a2e56', '#363862', '#48446c', '#605274', '#7a6078'], 195)];
  const r = rng(1742);
  SPLIT.far = WINS.length;
  { const p = pix(W, H); paintBlocks(p, r); paintGlassBox(p); paintCustomHouse(p); paintIndia(p, r); FAR_C = trim(p); }
  SPLIT.end = WINS.length;
  { const p = pix(W, H); paintQuincy(p); QM_C = trim(p); }
  HALL_D = { c: renderHall('dark'), x: 0, y: 0 };
  HALL_L = renderHall('lit');
  STATUE_C = renderStatue();
  GROUND_C = renderGround();
  PLANTER_C = renderPlanter(); FLOWERS_C = renderFlowers(); FESTOON = null;
  TREES = {
    T1: { o: renderTree(811, 2, 226, 96, 10, { n: 9, r: 44, ax: 1.5 }, 60, 0.6, 'T1'), key: 226 },
    T2: { o: renderTree(812, ...(() => { const s = spotAt(-58, 0); return [s.x, s.fy]; })(), 40, 2, { n: 7, r: 24, ax: 1 }, 26, 0.5, 'T2'), key: spotAt(-58, 0).fy },
    T4: { o: renderTree(813, ...(() => { const s = spotAt(-66, 8); return [s.x, s.fy]; })(), 36, -2, { n: 7, r: 21, ax: 1 }, 22, 0.45, 'T4'), key: spotAt(-66, 8).fy },
    V1: { o: renderTree(814, ...(() => { const s = spotAt(20, -10); return [s.x, s.fy]; })(), 12, 0, { n: 5, r: 9, ax: 1 }, 10, 0.7, 'V1'), key: 0 },
    V2: { o: renderTree(815, ...(() => { const s = spotAt(40, -12); return [s.x, s.fy]; })(), 11, 0, { n: 5, r: 8, ax: 1 }, 9, 0.7, 'V2'), key: 0 },
  };
  LAMP_POS = LAMPS.map(L => { const s = spotAt(L.x, L.y); return { ...s, k: SV * DEG / s.d, near: !!L.near }; });
  LAMP_N = renderLamp(LAMP_POS[0].k); LAMP_F = LAMP_POS.slice(1).map(L => renderLamp(L.k));
  CART_C = renderCart();
  PAV_C = renderPavilion(); PAV_MASK = null; FASCIA_TXT = renderFasciaText(); BOARD_C = renderBoard(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  PIG_SPR_C = { sit: PIG_SPR.map(rows => [sprite(rows, PIG_K), sprite(rows, PIG_K, true)]), fly: PIG_FLY.map(rows => sprite(rows, PIG_K)) };
  GULL_SPR = GULL.map(rows => sprite(rows, GULL_K));
  NPIG_SPR = NPIG.map(rows => [sprite(rows, NPIG_K), sprite(rows, NPIG_K, true)]);
  HOP_SPR = [HOP_K, HOP_KB].map(K => HOP.map(rows => [sprite(rows, K), sprite(rows, K, true)]));
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky and the far side ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.4 + 0.6 * Math.max(0, wave(t, s.k, s.ph))); ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  CLOUDS.forEach((c, i) => { const off = scroll(t, [2, 1, 1][i], W), y = [-4, 40, 88][i], o2 = i === 2 ? (off + 240) % W : off; ctx.drawImage(c, o2 - W, y); ctx.drawImage(c, o2, y); });
  for (const J of JETS) {                                                                         // jets climbing out over the harbour, high
    if (!inWin(t, J.t0, J.t1)) continue;
    const u = (J.t1 - t) / (J.t1 - J.t0), x = Math.round(lerp(-10, W + 10, u)), y = Math.round(34 - u * 16);
    dot(x, y, '#c8c8d0');
    if (frac(t / 1.2) < 0.12) { dot(x, y, '#ffffff'); halo(HL.tiny, x, y, 0.8); }
    dot(x + 1, y, frac(t / 1.6) < 0.5 ? '#ff4a3a' : '#4a2a2a');
  }
  for (const A of ARRIVE) {                                                                       // an arrival's landing light sinking towards Logan
    if (!inWin(t, A.t0, A.t1)) continue;
    const u = (t - A.t0) / (A.t1 - A.t0), x = Math.round(lerp(282, 336, u)), y = Math.round(lerp(150, 176, u * u * 0.6 + u * 0.4));
    const a = smooth(0, 0.15, u);
    ctx.globalAlpha = a; dot(x, y, '#ffffff'); ctx.globalAlpha = 1;
    halo(HL.landing, x, y, 0.7 * a);
    if (frac(t / 1.4) < 0.5) dot(x + 2, y, '#ff4a3a');
  }
}
const GREENWAY = [[1, 9, 0.1], [-1, 7, 0.35], [1, 11, 0.6], [-1, 8, 0.85]];
function drawFar(t) {
  blit(FAR_C); drawWins(SPLIT.far, SPLIT.end, t);
  halo(HL.ch, CH_COL, chRow(128), 0.7);
  dot(CH_COL, chRow(124), '#fff4d8'); dot(CH_COL - 1, chRow(124), '#e8dcb8');                       // the clock's lit face
  halo(HL.vista, 302, 178, 0.9);
  for (const [d, k, ph] of GREENWAY) {                                                            // traffic on Atlantic Avenue past the market's end
    const x = Math.round(lerp(d > 0 ? 296 : 330, d > 0 ? 330 : 296, frac(k * t / LOOP + ph)));
    dot(x, HZ + (d > 0 ? 1 : 0), d > 0 ? '#ff5a4a' : '#fff2d8');
  }
  blit(QM_C);
  for (const id of ['V1', 'V2']) { blit(TREES[id].o); drawBulbs(id, t, 0.6); }
}

/* ================= Per-frame: the hall ================= */
const hallLight = t => smooth(HALL.on, HALL.full, t) * (1 - smooth(HALL.dim, HALL.off, t));
function drawHall(t, crowd) {
  blit(HALL_D);
  const hall = hallLight(t);
  for (let i = 0; i < FH_WIN.length; i++) {
    const w = FH_WIN[i], a = w.storey === 0 ? 0.95 : hall;
    if (a < 0.02) continue;
    ctx.globalAlpha = a; ctx.drawImage(HALL_L, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1); ctx.globalAlpha = 1;
    const ww = w.x1 - w.x0 + 1;
    if (ww < 3) continue;
    const u = frac(t / (w.storey ? 24 : 16) + hash(i, 7) * 1), on = u < 0.22;                     // someone passing behind the glass
    if (on && crowd > 0 && (w.storey === 0 || hall > 0.6)) {
      const x = w.x0 + Math.round(u / 0.22 * (ww - 1)), top = w.y0 + Math.round((w.y1 - w.y0) * (w.storey ? 0.45 : 0.3));
      ctx.globalAlpha = 0.55 * a; R(x, top, Math.min(2, ww - 1), w.y1 - top + 1, '#2a1410'); ctx.globalAlpha = 1;
    }
  }
  halo(HL.cupola, CUPC, CUPR.belfry + 5, 0.9);
  for (const u of [0.12, 0.36, 0.64, 0.88]) { const [x, y] = proj(lerp(FH.NW[0], FH.SW[0], u), lerp(FH.NW[1], FH.SW[1], u) - 0.6, 0.3); halo(HL.flood, x, y, 0.8); }
  drawVane(t);
  drawRoost(t);
  drawBats(t);
}
// the grasshopper: at rest it heads into the evening's light easterly, seen in profile, head to the right; a puff turns
// it a little when the guide points; the breeze swings it round to show its other side to the floodlight, and back
const HOP = [['.......kk..', 'aa....k..k.', '..Ggggggggk', '...l.l..l..'], ['....kk.', 'a..k..k', '.Gggggk', '..l.l..'], ['a..a', '.kk.', 'kGGk', 'l..l']];
const HOP_K = { g: '#d8a030', G: '#7a5010', k: '#e8b848', a: '#b88a28', l: '#a87820' }, HOP_KB = { g: '#ffd860', G: '#a87018', k: '#fff0a0', a: '#e8c050', l: '#d8a838' };
let HOP_SPR;
function vaneAngle(t) {                                                                           // 0 profile right .. 1 end-on .. 2 profile left
  const swing = smooth(BREEZE.peak - 4, BREEZE.peak + 2, t) * (1 - smooth(BREEZE.fall - 2, BREEZE.fall + 5, t));
  const puff = 0.9 * env(t, GLINT[0], GLINT[1], 1.5);
  return Math.max(swing * 2, puff) + 0.12 * wave(t, 30);
}
function drawVane(t) {
  const a = vaneAngle(t), y = CUPR.vane;
  const frame = a < 0.45 ? 0 : a < 0.8 ? 1 : a < 1.2 ? 2 : a < 1.55 ? 1 : 0, flip = a < 1.2, bright = a > 1.55 || inWin(t, GLINT[0] + 1, GLINT[1] - 1);
  const s = HOP_SPR[bright ? 1 : 0][frame][flip ? 1 : 0];
  ctx.drawImage(s, CUPC - (s.width >> 1), y - 1);
  if (bright) halo(HL.gold, CUPC, y + 1, 0.5 + 0.3 * Math.sin(Math.PI * clamp((t - BREEZE.peak + 4) / (BREEZE.fall - BREEZE.peak + 8), 0, 1)));
}
/* ================= Per-frame: the square ================= */
// the string lights: each bulb twinkles on its own beat and sways with the breeze, the outer twigs most
function drawBulbs(tree, t, scale = 1) {
  const amp = (0.5 + 2.6 * breeze(t)) * scale, gust = wave(t, 96) * 0.5 + wave(t, 61, 0.3) * 0.5;
  ctx.fillStyle = '#fff0c0';
  for (let i = 0; i < BULBS.length; i++) {
    const b = BULBS[i];
    if (b.tree !== tree) continue;
    const sx = Math.round(b.sway * amp * (Math.sin(TAU * (48 * t / LOOP + b.ph)) * 0.6 + gust * 0.6)), tw = wave(t, b.k, b.ph);
    ctx.globalAlpha = tw > 0.55 ? 1 : tw > -0.4 ? 0.7 : 0.4;
    ctx.fillRect(b.x + sx, b.y, 1, 1);
    if ((i % 6) === 0 && tw > 0.2) { ctx.globalAlpha = 1; halo(HL.bulb, b.x + sx, b.y, 0.6); ctx.fillStyle = '#fff0c0'; }
  }
  ctx.globalAlpha = 1;
}
function drawTreeStir(o, tree, t) {                                                               // the crowns breathe: a few leaves catch the light
  blit(o);
  const k = 0.5 + 1.8 * breeze(t);
  ctx.fillStyle = '#4e6438';
  for (let i = 0; i < 40; i++) {
    const x = o.x + Math.floor(hash(i, 1, tree.length * 7) * o.c.width), y = o.y + Math.floor(hash(i, 2, tree.length * 7) * o.c.height * 0.75);
    if (wave(t, 30 + (i % 9) * 7, hash(i, 3)) * k > 1.05) ctx.fillRect(x, y, 1, 1);
  }
}
function drawLamp(L, img, t, gull) {
  ctx.drawImage(img.c, L.x - img.ax, L.fy - img.ay);
  if (gull) {                                                                                     // the herring gull on the finial
    const g = GULL_SPR[step(t, 48) % 5 === 2 ? 1 : 0];
    ctx.drawImage(g, L.x - 2, L.fy - img.ay + img.fin - 5);                                        // its feet on the finial's tip
  }
}
function drawLampLight(L, img, t, near) {
  const gy = L.fy - img.ay + img.gy, I = 0.94 + 0.04 * wave(t, 900, L.x * 0.01);
  for (const sx of [-img.arm, img.arm]) halo(near ? HL.globeN : HL.globeF, L.x + sx, gy, I);
  halo(near ? HL.lampN : HL.lampF, L.x, gy + 2, I);
  drawMoths(t, L.x - img.arm, gy, near ? 3 : 2, near ? 4 : 2);
}
const BB_FLICK = [[47.2, 48.4], [171.6, 172.3]];    // one of the sign's lamps stutters now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.sign, BB.x + 23, BB.y + 9, fl ? 0.4 : 0.7);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    if (fl) { ctx.globalAlpha = 0.3; R(BB.x + BB.w / 2, BB.y, BB.w / 2, BB.h, '#0a0c18'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const [lx, on] of [[BB.x + 13, true], [BB.x + BB.w - 8, !fl]]) { dot(lx, BB.y - 6, on ? '#fff6dc' : '#6a6050'); if (on) halo(HL.tiny, lx, BB.y - 5, 0.9); }
}
// pigeons at the viewer's feet, walking little loops and pecking; a few leaves and a paper cup skitter in the gusts
const NEAR_PIGS = [{ x: -71, y: -24.2, r: 0.9, k: 3, ph: 0.1 }, { x: -69.6, y: -25.6, r: 0.6, k: 4, ph: 0.55 }, { x: -66.4, y: -26.8, r: 1.1, k: 2, ph: 0.3 },
  { x: -72.6, y: -26.4, r: 0.7, k: 5, ph: 0.8 }];
// a feral pigeon, side on, head to the left: grey-blue, darker wing bars, the green-violet neck, pink feet
const NPIG = [['..hh......', '.bhnn.....', '...nggggt.', '...gGGGgtt', '....gggg..', '.....p.p..'],
  ['..........', '...nggg...', '..nnGGGgt.', '.hhgGGGgtt', 'bh..ggg...', '.....p.p..']];
const NPIG_K = { h: '#6e7280', b: '#2a2a30', n: '#4a7a72', g: '#6a6e7c', G: '#4e5260', t: '#3e4250', p: '#c87060' };
let NPIG_SPR;
function drawNearPigeon(px, fy, dir, peck, t) {
  const spr = NPIG_SPR[peck ? 1 : 0][dir > 0 ? 1 : 0];
  ctx.fillStyle = 'rgba(8,8,20,0.3)'; ctx.fillRect(Math.round(px) - 4, Math.round(fy) + 1, 8, 1);
  ctx.drawImage(spr, Math.round(px) - 5, Math.round(fy) - 5);
}
function drawNearLife(t) {
  NEAR_PIGS.forEach((g, i) => {
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.x + g.r * Math.sin(a), y = g.y + g.r * 0.5 * Math.sin(2 * a), sp = spotAt(x, y, 0.3);
    const dir = Math.cos(a) > 0 ? 1 : -1, peck = (step(t, 360) + i * 2) % 5 === 0;
    drawNearPigeon(sp.x, sp.fy, dir, peck, t);
  });
  for (let i = 0; i < 4; i++) {                                                                   // leaves and a cup in the gusts
    const f = (step(t, 24) + smooth(0.75, 1, frac(t / 10 + i * 0.27)) + i * 0.31) / 24;
    const u = frac(f * (i & 1 ? 1 : 2)), x = Math.round(lerp(500, -20, u)), y = 236 + i * 8 + Math.round(2 * Math.sin(u * 9 + i));
    if (i === 3) { R(x, y - 2, 2, 3, '#d8d4c8'); dot(x + 2, y - 1, '#a8a49a'); }
    else { dot(x, y, i ? '#8a5a2a' : '#6a6a2a'); dot(x + 1, y, i ? '#6a4420' : '#4a4a20'); }
  }
}
// pigeons roosting along the hall's cornice and its south side, shuffling now and then
const ROOST = [[0.16, 15.9, 'west'], [0.43, 15.9, 'west'], [0.82, 15.9, 'west'], [0.3, 18.3, 'rake'], [0.7, 18.2, 'rake'], [0.35, 15.9, 'south'], [0.7, 15.9, 'south']].map(([u, z, f], i) => {
  const F = f === 'south' ? FACES.south : FACES.west, x = lerp(F.A[0], F.B[0], u), y = lerp(F.A[1], F.B[1], u);
  const zz = f === 'rake' ? gableTop(u) + 0.15 : z, [c, r] = proj(x, y, zz);
  return { x: Math.round(c), y: Math.round(r), i };
});
function drawRoost(t) {
  for (const p of ROOST) {
    const sh = Math.round(1.2 * Math.sin(TAU * (6 * t / LOOP + p.i * 0.37))), up = (step(t, 120) + p.i * 5) % 17 === 0;
    R(p.x + sh, p.y - 2 - (up ? 1 : 0), 2, 2, '#5a5c66'); dot(p.x + sh + (p.i & 1 ? 2 : -1), p.y - 2 - (up ? 1 : 0), '#6a6c78');
  }
}
// Briny's at night: a festoon of bulbs under its fascia, the windows' light on the sidewalk dimming as the waiter passes
// inside
let FESTOON = null;
function drawPavLife(t, crowd) {
  if (!FESTOON) {
    FESTOON = [];
    for (let c = 340; c < W; c += 5) {
      const u = uAtCol(PAV.NW, PAV.W, c + 0.5), x = lerp(PAV.NW[0], PAV.W[0], u), y = lerp(PAV.NW[1], PAV.W[1], u), sag = Math.sin(Math.PI * frac((c - 340) / 20)) * 0.25;
      if (c > colOf(bearing(...PAV.W))) break;
      FESTOON.push([c, Math.round(proj(x, y, 3.2 - sag)[1])]);
    }
  }
  FESTOON.forEach(([x, y], i) => { const tw = wave(t, 50 + (i % 7) * 9, i * 0.13); dot(x, y, tw > 0.4 ? '#fff0c0' : tw > -0.5 ? '#e8c890' : '#a88a5a'); if (i % 3 === 0 && tw > 0.4) halo(HL.bulb, x, y, 0.7); });
  const w = here(RANK.waiter, crowd) && waiterIn(t);
  PAV_WIN.forEach(b => {
    if (!b) return;
    const ww = b.x1 - b.x0 + 1, pass = w ? clamp(1 - Math.abs(w.c - (b.x0 + b.x1) / 2) / (ww / 2 + 3), 0, 1) : 0;
    const fl = 0.24 - 0.13 * pass, gy = b.y1 + 14 + Math.round((b.x0 - 340) * 0.12);                // the window's light on the sidewalk
    const pw = ww + 10, ph = 9, hl = HL.pool;
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = fl * 3.4; ctx.drawImage(hl.c, b.x0 - 5, gy - 2, pw, ph); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  });
}
function drawPavilion(t, crowd, pitch, billboard) {
  halo(HL.win, 395, 172, 0.35);
  blit(PAV_C);
  drawBarInside(t, crowd);
  drawPavLife(t, crowd);
  blit(FASCIA_TXT);                                                                               // the fascia's lettering
  if (billboard) drawBoard(t, pitch);
}
function drawSquare(t, crowd, pitch, billboard, wx, wxc, reducedMotion) {
  const DL = [], put = (fy, fn) => DL.push([fy, fn]);
  put(ST.base + 2, () => { halo(HL.up, ST.cx - 2, ST.base - 10, 1); blit(STATUE_C); });
  put(TREES.T2.key, () => { drawTreeStir(TREES.T2.o, 'T2', t); drawBulbs('T2', t); });
  put(TREES.T4.key, () => { drawTreeStir(TREES.T4.o, 'T4', t); drawBulbs('T4', t); });
  LAMP_POS.forEach((L, i) => put(L.fy, () => drawLamp(L, i ? LAMP_F[i - 1] : LAMP_N, t, i === 0)));
  put(CART.base, () => {
    blit(CART_C);
    CART_C.bulbs.forEach(([x, y], i) => { dot(x, y, wave(t, 40 + i * 3, i * 0.2) > -0.6 ? '#fff0c0' : '#a89870'); });
    halo(HL.cart, CART.cx, CART.base - 20, 0.8);
  });
  if (here(RANK.vendor, crowd)) put(VEND.fy, () => drawVendor(t));
  if (here(RANK.buyer, crowd)) put(CART.base + 0.5, () => drawBuyer(t));
  if (here(RANK.busker, crowd)) put(BUSK.fy, () => drawBusker(t, crowd));
  AUD.forEach((a, i) => { if (here(a.rank, crowd)) put(a.fy + (a.sit ? 0.3 : 0), () => drawAud(a, i, t)); });
  if (here(RANK.kid, crowd)) put(PARENT.fy, () => drawKid(t));
  put(PIG_GROUND[0].fy - 0.5, () => drawPigeons(t));
  if (here(RANK.ice, crowd)) put(ICE_SEAT[0].fy + 0.2, () => drawIceCouple(t));
  if (here(RANK.sitter, crowd)) put(SITTER.fy + 0.1, () => drawSitter(t));
  if (here(RANK.photo, crowd)) put(PHOTO.fy, () => drawPhotographer(t));
  put(TABLE.fy, () => drawPatio(t, crowd));
  if (here(RANK.guide, crowd) && inWin(t, TOUR.t0, TOUR.t1)) put(spotAt(...trailAt(tourM(t).x, 0)).fy, () => drawTour(t, crowd));
  put(laneSpot(LANE_A, 0.5).fy, () => drawWalkers(t, crowd, 'A'));
  put(laneSpot(LANE_B, 0.5).fy, () => drawWalkers(t, crowd, 'B'));
  put(laneSpot(LANE_P, 0.5).fy - 0.5, () => drawPedicab(t, crowd));
  put(204.5, () => drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion));
  put(219, () => drawPavilion(t, crowd, pitch, billboard));
  put(246, () => { ctx.drawImage(PLANTER_C, 76, 234); const sw = Math.round(0.6 * wave(t, 40) + 1.2 * breeze(t) * wave(t, 97, 0.2)); ctx.drawImage(FLOWERS_C, 76 + sw, 234); });
  put(232, () => drawNearLife(t));
  put(TREES.T1.key, () => { drawTreeStir(TREES.T1.o, 'T1', t); drawBulbs('T1', t); });
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  LAMP_POS.forEach((L, i) => drawLampLight(L, i ? LAMP_F[i - 1] : LAMP_N, t, i === 0));
}
function drawAud(a, i, t) {
  const clap = clapping(t), film = inWin(t, ACT.clubs4, ACT.clap + 3);
  let x = a.x, fy = a.fy, view = a.view, dir = a.dir, aL = 0, aR = 0, lean = 0, P = a.P, stepF = 0;
  if (a.vol && inWin(t, ACT.vol, ACT.up + 3)) {                                                   // the volunteer steadies the unicycle
    const go = smooth(ACT.vol, ACT.vol + 3, t) * (1 - smooth(ACT.up, ACT.up + 3, t)), s = VOL_SPOT;
    x = Math.round(lerp(a.x, s.x, go)); fy = Math.round(lerp(a.fy, s.fy, go)); P = a.PL(Math.round(lerp(a.h, s.h, go)));
    const moving = go > 0.02 && go < 0.98;
    view = moving ? 2 : 1; dir = (t < ACT.mount ? 1 : -1) * (Math.sign(s.x - a.x) || 1); aR = moving ? 0 : 3; stepF = moving ? Math.floor(t * 3.2) & 1 : 0;
  } else if (clap && !a.sit) { const c = step(t, 1440) & 1; aL = c ? 2 : 3; aR = c ? 3 : 2; if (view === 2) aR = 1; }
  else if (clap) { aL = 1; aR = 1; }
  else if (film && a.film && view !== 2) { aL = 2; aR = 2; }
  else {
    const h = hash(step(t, 40), i, 71);
    if (h < 0.15) lean = view === 1 ? 1 : -1;
    if (h > 0.85 && view !== 2) aR = 2;
    if (a.tip && inWin(t, ACT.hat + 4 + a.tip, ACT.hat + 6.5 + a.tip)) aR = 3;                    // a dollar into the hat
  }
  person(x, fy, P, { view, dir, aL, aR, lean, sit: a.sit, step: stepF });
  if (film && a.film && view !== 2 && !clap) {                                                    // phones up for the finale: the screens glow
    const ty = tyOf(P, fy) - P.hh - 2;
    R(x - 1, ty, 3, 2, view === 0 ? '#9ad0ff' : '#2a3040'); if (view === 0) halo(HL.screen, x, ty + 1, 0.6);
  }
}
const VOL_SPOT = spotAt(CIRCLE.x + 1.5, CIRCLE.y - 1.0);
AUD.forEach((a, i) => { a.PL = lookAt(...a.L); a.film = i === 0 || i === 5 || i === 6; a.tip = i === 0 ? 3 : i === 1 ? 9 : i === 3 ? 6 : 0; });

/* ================= Tap spots ================= */
// the cupola's bell, the Great Hall's middle window on the west front, the pigeons roosting on the cornice
const SPOTS = (() => {
  const u = 0.5, x = lerp(FH.NW[0], FH.SW[0], u), y = lerp(FH.NW[1], FH.SW[1], u), [wc, wt] = proj(x, y, 9.3), [, wb] = proj(x, y, 6.0);
  const rb = ROOST[1];
  return [
    { kind: 'bell', x: CUPC - 6, y: CUPR.vane, w: 13, h: CUPR.base - CUPR.vane },
    { kind: 'window', x: Math.round(wc) - 4, y: Math.round(wt), w: 8, h: Math.round(wb - wt) },
    { kind: 'birds', x: rb.x - 3, y: rb.y - 5, w: 8, h: 5 },
  ];
})();

/* ================= Weather (a visitor's pick; the city's own is clear) ================= */
const WEATHER = weatherPlans([]);
const WX_LAND = [[HZ + 2, 197], [198, 212], [H + 4, H + 4]];
const WX_LIGHTS = [{ x: 117, y: 150, rx: 26, ry: 40 }, { x: 170, y: 172, rx: 14, ry: 18 }, { x: 248, y: 172, rx: 14, ry: 18 }, { x: 395, y: 165, rx: 50, ry: 42 }, { x: 87, y: 186, rx: 14, ry: 14 }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 8, rx: 30, ry: 16, col: '#ffe8c0' }] };

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on Briny's roof sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own (clear) when left out; crowd: the share
// of the people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  blit(GROUND_C);
  drawFar(t);
  drawHall(t, crowd);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawSquare(t, crowd, pitch, billboard, wx, wxc, reducedMotion);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'boston-faneuil-hall',
  name: 'Boston',
  tagline: 'Buskers by the grasshopper',
  timeZone: 'America/New_York',
  accent: '#44bb11',                                   // the Green Line's green, written out: tools/seo/seo.mjs reads it from the source
  focusX: 203,
  landmark: [130, 277],                                // Faneuil Hall: the west front's north corner to the south side's east corner
  landmarkTop: CUPR.vane,                              // the grasshopper
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: SPOTS,
  ambience: { crowd: 0.16, traffic: 0.12, wind: 0.1 },
  events: [
    { t: CLAPS[0][0], type: 'chime', x: BUSK.x, gain: 0.08 },
    { t: 36, type: 'gull', x: 117, gain: 0.07 },
    { t: ACT.clap, type: 'chime', x: BUSK.x, gain: 0.16 },
    { t: CLAPS[2][0], type: 'chime', x: BUSK.x, gain: 0.08 },
    { t: GLINT[0] + 1, type: 'chime', x: CUPC, gain: 0.08 },
    { t: 168, type: 'gull', x: 117, gain: 0.07 },
    { t: CLAPS[3][0], type: 'chime', x: BUSK.x, gain: 0.08 },
    { t: BREEZE.peak + 3, type: 'chime', x: CUPC, gain: 0.08 },
    { t: CLAPS[4][0], type: 'chime', x: BUSK.x, gain: 0.08 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
